import React, { useState, useContext, useEffect, useRef } from 'react';
import { Layout } from './Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import {
  Brain,
  ArrowLeft,
  Save,
  Play,
  Copy,
  Trash2,
  TestTube,
  Eye,
  Edit,
  Home,
  Settings,
  FileText,
  Code,
  MessageSquare,
  Target,
  ListChecks,
  BookOpen,
  Archive,
  CheckCircle,
  AlertTriangle,
  Clock,
  GitBranch,
  Tag,
  X,
  Download,
  Upload,
  MoreHorizontal,
  Activity,
  TrendingUp,
  Users,
  Plus,
  ChevronDown,
  ChevronUp,
  FileUp,
  Type,
  Table,
  FileCode,
  FileOutput,
  Info,
  ArrowLeftRight,
  Columns2,
  Maximize2,
  Minimize2,
  Search,
  Hash,
  ZoomIn,
  ZoomOut,
  GitCommit,
  Minus,
  SquarePlus
} from 'lucide-react';
import { toast } from 'sonner';
import { AppContext } from '../src/App';
import { useAuth } from './AuthContext';
import { apiClient } from '@/Utils/apiClient';
import { useModelThinkingConfig } from '../src/hooks/useModelThinking';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/routes/config';


// Mock data for the prompt - this would come from your backend
const mockPromptData = {
  id: 'prompt-001',
  name: 'Customer Information Extraction',
  description: 'Extract customer information from business documents',
  category: 'Healthcare',
  status: 'Active',
  systemPrompt: 'You are an AI assistant specialized in extracting customer information from business documents.',
  promptText: 'Please extract customer information from the uploaded document.',
  linkedDocumentType: 'Clinical Notes',
  outputFormat: 'JSON',
  targetModel: 'GPT-4',
  temperature: 0.7,
  maxTokens: 1000,
  version: '1.2',
  tags: ['extraction', 'healthcare', 'documents'],
  successRate: 94.2,
  avgResponseTime: 2.3,
  usageCount: 1248,
  conversationRoles: [
    {
      id: 'system',
      type: 'System',
      content: 'You are an AI assistant specialized in extracting customer information from business documents.',
      isCollapsed: false
    },
    {
      id: 'user',
      type: 'User',
      content: 'Please extract customer information from the uploaded document.',
      isCollapsed: false
    }
  ],
  tasks: [
    'Extract customer name, contact details, and company information',
    'Identify any special requirements or notes',
    'Format the output as structured JSON'
  ],
  instructions: [
    'Ensure all extracted information is accurate',
    'Use consistent formatting for addresses and phone numbers',
    'Mark any uncertain information with a confidence score'
  ]
};

interface ConversationRole {
  id: string;
  type: string;
  content: string;
  isCollapsed: boolean;
}

interface TestResult {
  id: string;
  timestamp: Date;
  success: boolean;
  responseTime: number;
  output: string;
  model: string;
}

interface VersionHistoryItem {
  date?: Date;
  author?: string;
  created_at?: string | number | Date | undefined
  avg_resp_ms?: String | null
  changes?: String | undefined
  description?: String | undefined
  id?: String | undefined
  is_active?: boolean | undefined
  linked_document_type?: String | undefined
  output_format?: String | undefined
  status?: String | undefined
  version?: String | undefined
  promptContent?: string | undefined;
}

// Enhanced Code Editor Component with Line Numbers
function CodeEditor({
  value,
  onChange,
  placeholder,
  language = 'json',
  className = '',
  disabled = false
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  language?: string;
  className?: string;
  disabled?: boolean;
}) {
  const lines = value?.split('\n');
  const lineCount = Math.max(lines?.length, 15);

  return (
    <div className={`relative border rounded-lg overflow-hidden bg-gray-50 ${className}`}>
      <div className="flex">
        {/* Line Numbers */}
        <div className="bg-gray-100 border-r border-gray-200 px-3 py-3 select-none">
          <div className="font-mono text-xs text-gray-500 leading-5">
            {Array.from({ length: lineCount }, (_, i) => (
              <div key={i + 1} className="text-right">
                {i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 relative">
          <Textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            rows={15}
            className={`
              font-mono text-sm border-none bg-transparent resize-none outline-none
              ${language === 'json' ? 'text-blue-900' : 'text-gray-900'}
              p-3 leading-5
            `}
            style={{
              minHeight: `${lineCount * 20 + 24}px`,
              resize: 'vertical'
            }}
          />
        </div>
      </div>
    </div>
  );
}

// File Upload Component
function FileUploadInput({
  onFileSelect,
  disabled = false
}: {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
}) {
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFileSelect(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onFileSelect(files[0]);
    }
  };

  return (
    <div
      className={`
        border-2 border-dashed rounded-lg p-8 text-center transition-colors
        ${dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-gray-400'}
      `}
      onDrop={handleDrop}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
    >
      <FileUp className="w-8 h-8 text-gray-400 mx-auto mb-2" />
      <p className="text-gray-600 mb-2">Drag and drop a file here, or click to select</p>
      <input
        type="file"
        onChange={handleFileInput}
        disabled={disabled}
        className="hidden"
        id="file-upload"
        accept=".txt,.json,.csv,.pdf,.doc,.docx"
      />
      <Button
        variant="outline"
        onClick={() => document.getElementById('file-upload')?.click()}
        disabled={disabled}
        className="mt-2"
      >
        <Upload className="w-4 h-4 mr-2" />
        Select File
      </Button>
    </div>
  );
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'Testing':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Draft':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'Archived':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// Get output format details
const getOutputFormatDetails = (format: string) => {
  const formatDetails = {
//     'JSON': {
//       description: 'Structured data format ideal for APIs and data exchange',
//       icon: Code,
//       example: `{
//   "patient_name": "John Doe",
//   "diagnosis": "Type 2 Diabetes",
//   "medications": [
//     {
//       "name": "Metformin",
//       "dosage": "500mg",
//       "frequency": "twice daily"
//     }
//   ],
//   "next_appointment": "2024-01-15"
// }`,
//       guidelines: [
//         'Use consistent field naming (snake_case recommended)',
//         'Include data types for validation',
//         'Nest related data logically',
//         'Use arrays for multiple items'
//       ]
//     },
'JSON': {
      description: 'Structured data format ideal for APIs and data exchange',
      icon: Code,
      example: `{
  "patient_name": String,
  "diagnosis": String,
  "medications": [
    {
      "name": String,
      "dosage": String,
      "frequency": String
    }
  ],
  "next_appointment": String
}`,
      guidelines: [
        'Use consistent field naming (snake_case recommended)',
        'Include data types for validation',
        'Nest related data logically',
        'Use arrays for multiple items'
      ]
    },
'XML': {
      description: 'Markup language for structured documents and data exchange',
      icon: FileCode,
      example: `<?xml version="1.0" encoding="UTF-8"?>
<patient>
  <name>John Doe</name>
  <diagnosis>Type 2 Diabetes</diagnosis>
  <medications>
    <medication>
      <name>Metformin</name>
      <dosage>500mg</dosage>
      <frequency>twice daily</frequency>
    </medication>
  </medications>
  <next_appointment>2024-01-15</next_appointment>
</patient>`,
      guidelines: [
        'Use descriptive element names',
        'Include XML declaration',
        'Properly nest elements',
        'Use attributes for metadata'
      ]
    },
    'Structured Text': {
      description: 'Human-readable formatted text with consistent structure',
      icon: FileText,
      example: `PATIENT INFORMATION
Name: John Doe
Diagnosis: Type 2 Diabetes

MEDICATIONS:
- Metformin 500mg (twice daily)

NEXT APPOINTMENT: 2024-01-15

NOTES:
Patient is responding well to current treatment plan.`,
      guidelines: [
        'Use clear section headers',
        'Maintain consistent formatting',
        'Include bullet points for lists',
        'Use proper spacing and indentation'
      ]
    },
    'CSV': {
      description: 'Comma-separated values for tabular data and spreadsheets',
      icon: Table,
      example: `patient_id,name,diagnosis,medication,dosage,frequency,next_appointment
PT001,John Doe,Type 2 Diabetes,Metformin,500mg,twice daily,2024-01-15
PT002,Jane Smith,Hypertension,Lisinopril,10mg,once daily,2024-01-20`,
      guidelines: [
        'Include descriptive headers',
        'Escape commas in data fields',
        'Use consistent date formats',
        'Avoid line breaks in cells'
      ]
    },
    'Formatted Text': {
      description: 'Rich text with formatting for reports and documentation',
      icon: Type,
      example: `**PATIENT REPORT**

*Patient:* John Doe  
*Date:* January 10, 2024  
*Diagnosis:* Type 2 Diabetes  

**Current Medications:**
• Metformin 500mg - twice daily
• Lifestyle modifications recommended

**Follow-up:**
Next appointment scheduled for January 15, 2024.`,
      guidelines: [
        'Use markdown or similar formatting',
        'Emphasize important information',
        'Structure with headers and lists',
        'Include proper spacing'
      ]
    }
  };

  return formatDetails[format as keyof typeof formatDetails] || formatDetails['JSON'];
};
interface DiffLine {
  type: 'add' | 'remove' | 'context';
  content: string;
  oldLineNumber?: number;
  newLineNumber?: number;
}

interface DiffHunk {
  oldStart: number;
  oldCount: number;
  newStart: number;
  newCount: number;
  lines: DiffLine[];
}

const createUnifiedDiff = (oldText: string, newText: string): DiffHunk[] => {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  
  const hunks: DiffHunk[] = [];
  let oldIndex = 0;
  let newIndex = 0;
  
  // Simple diff algorithm - in production, you'd use Myers algorithm
  while (oldIndex < oldLines.length || newIndex < newLines.length) {
    const lines: DiffLine[] = [];
    const hunkOldStart = oldIndex + 1;
    const hunkNewStart = newIndex + 1;
    let hunkOldCount = 0;
    let hunkNewCount = 0;
    
    const contextSize = 3;
    let hasChanges = false;
    
    // Process changes
    while (oldIndex < oldLines.length || newIndex < newLines.length) {
      if (oldIndex >= oldLines.length) {
        // Only new lines left
        lines.push({
          type: 'add',
          content: newLines[newIndex],
          newLineNumber: newIndex + 1
        });
        newIndex++;
        hunkNewCount++;
        hasChanges = true;
      } else if (newIndex >= newLines.length) {
        // Only old lines left
        lines.push({
          type: 'remove',
          content: oldLines[oldIndex],
          oldLineNumber: oldIndex + 1
        });
        oldIndex++;
        hunkOldCount++;
        hasChanges = true;
      } else if (oldLines[oldIndex] === newLines[newIndex]) {
        // Lines are the same
        lines.push({
          type: 'context',
          content: oldLines[oldIndex],
          oldLineNumber: oldIndex + 1,
          newLineNumber: newIndex + 1
        });
        oldIndex++;
        newIndex++;
        hunkOldCount++;
        hunkNewCount++;
        
        // If we've added context after changes, we can break
        if (hasChanges && lines.filter(l => l.type === 'context').length >= contextSize) {
          break;
        }
      } else {
        // Lines are different - show both as remove and add
        lines.push({
          type: 'remove',
          content: oldLines[oldIndex],
          oldLineNumber: oldIndex + 1
        });
        lines.push({
          type: 'add',
          content: newLines[newIndex],
          newLineNumber: newIndex + 1
        });
        oldIndex++;
        newIndex++;
        hunkOldCount++;
        hunkNewCount++;
        hasChanges = true;
      }
    }
    
    if (lines.length > 0) {
      hunks.push({
        oldStart: hunkOldStart,
        oldCount: hunkOldCount,
        newStart: hunkNewStart,
        newCount: hunkNewCount,
        lines
      });
    }
    
    break; // For simplicity, create one hunk
  }
  
  return hunks;
};

export function PromptDetailView() {
  const { selectedPromptData, sideBarItems, setCurrentView, setSelectedPromptData } = useContext(AppContext);

  const { user,token } = useAuth();

  const promptsMenu = sideBarItems.find((item: any) => item.name === "Prompts");

  const hasPermission = (permission: string) => {
    return promptsMenu?.userPermissions?.includes(permission);
  };

  // Use mock data if no prompt is selected (for development)
  // const promptData = selectedPromptData || mockPromptData;

    const location = useLocation();
    const passedRunData = location.state?.prompt
    const navigate = useNavigate();

    useEffect(() => {
      if(!selectedPromptData){
        console.log(passedRunData)
        setSelectedPromptData(passedRunData)
      }
    },[])

    console.log(passedRunData, "passedRunData")
  const promptData = selectedPromptData || passedRunData;

  // Check if this is a new prompt being created
  const isNewPrompt = (selectedPromptData || passedRunData) ? false : true;

  const [isEditing, setIsEditing] = useState(isNewPrompt); // Start in edit mode for new prompts
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [category, setCategory] = useState<any>([]);
  const [models, setModels] = useState<Array<{ id: string; name: string }>>([]);
  const [touched, setTouched] = useState(false);
  // Version history state
  const [versionHistory, setVersionHistory] = useState<VersionHistoryItem[]>([]
    // isNewPrompt ? [] : [
    //   { version: '1.2', date: new Date(), author: 'Dr. Sarah Mitchell', changes: 'Updated prompt template for better accuracy' },
    //   { version: '1.1', date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), author: 'John Smith', changes: 'Added system prompt for healthcare context' },
    //   { version: '1.0', date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), author: 'Dr. Sarah Mitchell', changes: 'Initial version created' }
    // ]
  );
  // Version comparison state

  const [showVersionComparison, setShowVersionComparison] = useState(false);

  const [comparisonMode, setComparisonMode] = useState<'split' | 'fullwidth'>('split');

  const [comparisonView, setComparisonView] = useState<'side-by-side' | 'unified'>('side-by-side');

  const [comparisonTab, setComparisonTab] = useState<'prompt' | 'output'>('prompt');

  const [showLineNumbers, setShowLineNumbers] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');

  const [selectedVersions, setSelectedVersions] = useState<{ version1: string; version2: string }>({

    version1: '',

    version2: ''

  });
// json schema comparison state
const [jsonStats, setJsonStats] = useState({
  v1:{numberOfFields:"", numberOfArray:"", nestedObjects:""},
  v2: {numberOfFields:"", numberOfArray:"", nestedObjects:""}
})
  // Thinking budget input (string) so user can clear and type; validated on blur / save
  const [thinkingBudgetInput, setThinkingBudgetInput] = useState('');
  const [thinkingBudgetError, setThinkingBudgetError] = useState<string | null>(null);
  const BUDGET_MIN = 1000;
  const BUDGET_MAX = 24576;

  // Form data state
  const [formData, setFormData] = useState({
    name: promptData?.name || '',
    description: promptData?.description || '',
    category: promptData?.category || '',
    status: promptData?.status || 'Draft',
    systemPrompt: promptData?.systemPrompt || '',
    promptText: promptData?.promptText || '',
    linkedDocumentType: promptData?.linkedDocumentType || '',
    outputFormat: promptData?.outputFormat || 'JSON',
    targetModel: promptData?.targetModel || '',
    enableThinking: promptData?.enableThinking ?? false,
    thinkingLevel: (promptData as any)?.thinkingLevel || '',
    thinkingBudget: (promptData as any)?.thinkingBudget ?? null,
    temperature: promptData?.temperature ?? 0,
    maxTokens: promptData?.maxTokens || 5000,
    conversationRoles: promptData?.conversationRoles || [
      {
        id: 'system',
        role: 'System',
        content: '',
        isCollapsed: false
      },
      // {
      //   id: 'user',
      //   role: 'User',
      //   content: '',
      //   isCollapsed: false
      // }
    ],
    tasks: promptData?.tasks || [''],
    instructions: promptData?.instructions || [''],
    customOutputExample: promptData?.output_template || ''
  });

  // Fetch per-model thinking configuration so UI can adapt dynamically.
  const {
    config: modelThinkingConfig,
    loading: modelThinkingLoading,
    error: modelThinkingError,
  } = useModelThinkingConfig(formData?.targetModel || '', token);

  const modelThinkingReady = !!modelThinkingConfig && !modelThinkingLoading && !modelThinkingError;
  const modelThinkingModeEnabled = modelThinkingConfig?.thinkingModeEnabled ?? false;
  const normalizedThinkingLevels =
    Array.isArray(modelThinkingConfig?.thinkingLevelOptions)
      ? modelThinkingConfig!.thinkingLevelOptions!
          .map((opt) => String(opt).toLowerCase())
          .filter((opt) => ['minimal', 'low', 'medium', 'high'].includes(opt))
      : null;

  const apiBudgetMin = modelThinkingConfig?.budgetRange?.min;
  const apiBudgetMax = modelThinkingConfig?.budgetRange?.max;
  const effectiveBudgetMin = typeof apiBudgetMin === 'number' ? apiBudgetMin : BUDGET_MIN;
  const effectiveBudgetMax = typeof apiBudgetMax === 'number' ? apiBudgetMax : BUDGET_MAX;

  const isBudgetNumeric =
    modelThinkingConfig?.thinkingBudgetType === 'numeric' &&
    !!modelThinkingConfig?.budgetRange &&
    typeof modelThinkingConfig.budgetRange.min === 'number' &&
    typeof modelThinkingConfig.budgetRange.max === 'number' &&
    effectiveBudgetMin <= effectiveBudgetMax;

  const modelBudgetMin = effectiveBudgetMin;
  const modelBudgetMax = effectiveBudgetMax;

  // If the selected model doesn't support thinking, force-disable it.
  useEffect(() => {
    if (!modelThinkingReady) return;
    if (modelThinkingModeEnabled) return;
    if (formData?.enableThinking) {
      setFormData((prev) => ({ ...prev, enableThinking: false }));
    }
  }, [modelThinkingReady, modelThinkingModeEnabled]);

  // Keep thinkingLevel in sync with the model's supported options.
  useEffect(() => {
    if (!modelThinkingReady) return;
    if (!normalizedThinkingLevels || normalizedThinkingLevels.length === 0) {
      // Model has no thinking level support – clear the value.
      if (formData?.thinkingLevel) {
        setFormData((prev) => ({ ...prev, thinkingLevel: '' }));
      }
      return;
    }
    if (!normalizedThinkingLevels.includes(formData?.thinkingLevel)) {
      setFormData((prev) => ({ ...prev, thinkingLevel: normalizedThinkingLevels[0] as any }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelThinkingReady, normalizedThinkingLevels?.join('|')]);

  const thinkingUiLocked =
    !isEditing || modelThinkingLoading || !!modelThinkingError || !modelThinkingConfig;

  // Testing state
  const [testInputType, setTestInputType] = useState<'json' | 'text' | 'csv' | 'file'>('json');
  const [testInputs, setTestInputs] = useState({
    json: JSON.stringify({
      patient_name: 'John Doe',
      document_content: 'Sample clinical note content...',
      symptoms: ['fever', 'cough', 'fatigue'],
      vital_signs: {
        temperature: '101.3°F',
        blood_pressure: '120/80',
        heart_rate: 85
      }
    }, null, 2),
    text: 'Sample text input for processing...\n\nPatient: John Doe\nSymptoms: fever, cough, fatigue\nNotes: Patient reports symptoms started 3 days ago.',
    csv: 'patient_id,name,age,symptoms,notes\n001,John Doe,45,"fever,cough",Started 3 days ago\n002,Jane Smith,32,"headache,nausea",Ongoing for 1 week',
    file: null as File | null
  });
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  // syn scroll in compare prompt

  const baseDiv=useRef<HTMLDivElement>(null);
  const compareDiv=useRef<HTMLDivElement>(null);

  // compareOutput Shema
  const baseOutput=useRef<HTMLDivElement>(null);
  const compareOutput=useRef<HTMLDivElement>(null);

  // for focus the Prompt name
  const nameInputRef = useRef<HTMLInputElement>(null);

 const cleanJSONCodeFn = (Value: any) => {

    try {
      const unescaped = Value
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');

      const cleaned = unescaped.replace(/\/\/.*$/gm, '');
        // Add quotes around unquoted keys: name: -> "name":
    const withQuotedKeys = cleaned.replace(/([{,]\s*)([a-zA-Z0-9_]+)\s*:/g, '$1"$2":');

    // Add quotes around unquoted string values (naive approach)
    const withQuotedValues = withQuotedKeys.replace(/:\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*([,}])/g, ':"$1"$2');
      return withQuotedValues;
      
    } catch (err) {
      console.error('Invalid JSON:', err);
    }
  }
  const fetchData = async () => {
    try {

      const response = await fetch(`${import.meta.env.VITE_API_URL}prompts/${selectedPromptData?.prompt_id || passedRunData?.prompt_id}/versions/${selectedPromptData?.version_id || passedRunData?.version_id}`, {


        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        // body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      const val = data?.data

      const dataz = {
        name: val?.name || '',
        description: val.description || '',
        category: val.category || '',
        status: val.status || 'Draft',
        systemPrompt: val.systemPrompt || '',
        promptText: val.promptText || '',
        linkedDocumentType: val.linkedDocumentType || '',
        outputFormat: val.outputFormat || 'JSON',
        targetModel: val?.modelType || '',
        enableThinking: val?.enableThinking ?? val?.enable_thinking ?? false,
        thinkingLevel: val?.thinkingLevel ?? val?.thinking_level ?? '',
        thinkingBudget: val?.thinkingBudget ?? val?.thinking_budget ?? null,
        temperature: val?.temperature ?? val?.snapshot?.parameters?.temperature ?? 0,
        maxTokens: val?.snapshot?.parameters?.max_tokens || 1000,
        conversationRoles: val?.snapshot?.conversation_roles || [],
        tasks: val?.snapshot?.tasks || [''],
        instructions: val?.snapshot?.instructions || [''],
        customOutputExample: val?.outputTemplate|| '',
        successRate: val?.successRate || 0,
        avgResponseTime: val?.avgRespSec || 0,
        usageCount: val?.usageCount || 0,
        version: val?.version || '1.0'
      }

      setFormData(dataz);
      setThinkingBudgetInput(dataz?.thinkingBudget != null ? String(dataz.thinkingBudget) : '');
      setThinkingBudgetError(null);
     promptData.name= dataz?.name;
      promptData.description = dataz?.description;
      promptData.category = dataz?.category;  
      promptData.status = dataz?.status;
      promptData.systemPrompt = dataz?.systemPrompt;
      promptData.promptText = dataz?.promptText;
      promptData.linkedDocumentType = dataz?.linkedDocumentType;
      promptData.outputFormat = dataz?.outputFormat;
      promptData.targetModel = dataz?.targetModel;
      promptData.enableThinking = dataz?.enableThinking;
      (promptData as any).thinkingLevel = dataz?.thinkingLevel;
      (promptData as any).thinkingBudget = dataz?.thinkingBudget;
      promptData.temperature = dataz.temperature;
      promptData.maxTokens = dataz?.maxTokens;
      promptData.conversationRoles = dataz?.conversationRoles;
      promptData.tasks = dataz?.tasks;
      promptData.instructions = dataz?.instructions;
      promptData.customOutputExample = dataz?.customOutputExample;
      promptData.successRate = dataz.successRate;
      promptData.avgResponseTime = dataz.avgResponseTime;
      promptData.usageCount = dataz.usageCount;
      promptData.version = dataz.version;
      // const newData = await generateMockHarvestData(data)
      return data;
    } catch (error: any) {

      return null;
    }
  };
  const fetchPromtVersion = async () => {
    try {

      const response = await fetch(`${import.meta.env.VITE_API_URL}prompts/${selectedPromptData?.prompt_id || passedRunData?.prompt_id}`, {


        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        // body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      const val = data?.data?.versions
      console.log(val, "version_val")
      setVersionHistory(val)


      // const newData = await generateMockHarvestData(data)
      return data;
    } catch (error: any) {
toast.error(error.message||'Request failed')
      return null;
    }
  };

  const fetchCategory = async () => {
    try {

      const response = await fetch(`${import.meta.env.VITE_API_URL}prompts/category`, {


        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }

      const val = data?.data
      console.log(val, "version_val")
      setCategory(val)


      // const newData = await generateMockHarvestData(data)
      return data;
    } catch (error: any) {
    toast.error(error.message||'Request failed')
      return null;
    }
  };

  const fetchModels = async () => {
    try {
      const response = await apiClient('prompts/ai/models', {
        method: 'GET',
        token
      });
      const data = await response.json();
      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch models');
      }
      setModels(data?.data || []);
      return data;
    } catch (error: any) {
      console.error('Models fetch failed:', error.message);
      toast.error('Failed to fetch AI models');
      return null;
    }
  };

  useEffect(() => {
    if(!selectedPromptData){
     nameInputRef.current?.focus();
      fetchCategory()
      fetchModels()
      return
    }
    fetchData()
    fetchPromtVersion()
    fetchCategory()
    fetchModels()
    
  }, []);

  // When models load, ensure targetModel is a valid model id (for Select compatibility)
  useEffect(() => {
    if (models.length === 0 || !formData.targetModel) return;
    const matchById = models.find((m) => m.id === formData.targetModel);
    const matchByName = models.find((m) => m.name === formData.targetModel);
    if (!matchById && matchByName) {
      setFormData((prev) => ({ ...prev, targetModel: matchByName.id }));
    }
  }, [models]);

  // When Thinking Mode is turned on (or budget type changes), populate/clear the budget input.
  useEffect(() => {
    if (formData?.enableThinking) {
      if (!isBudgetNumeric) {
        // Budget is N/A for this model — clear the field.
        setThinkingBudgetInput('');
        setThinkingBudgetError(null);
      } else if (isNewPrompt) {
        // New prompts: always start empty so the user must enter a value.
        setThinkingBudgetInput('');
        setThinkingBudgetError(null);
      } else {
        // Existing prompts: show the previously saved value.
        const saved = formData?.thinkingBudget;
        setThinkingBudgetInput(saved != null ? String(saved) : '');
      }
    }
  }, [formData?.enableThinking, isBudgetNumeric]);
const fetchPromptContent= async (versionId:VersionHistoryItem ) => {
  try{
    const response = await fetch(`${import.meta.env.VITE_API_URL}prompts/${selectedPromptData?.prompt_id || passedRunData?.prompt_id}/versions/${versionId?.id}`, {


        method: 'GET',
        cache: 'no-store',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        // body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Request failed');
      }
      setVersionHistory(prev =>prev.map(item => item.id === versionId?.id // match by version id
      ? { ...item, promptContent: data?.data?.snapshot?.conversation_roles?.[0]?.content || '' }
      : item
  ))
  }
  catch(err){
    console.log(err)
    toast.error('Error fetching prompt content');
  }
}

  // Dynamic input types based on output format
  const getAvailableInputTypes = (outputFormat: string) => {
    const inputTypeConfig = {
      'JSON': [
        { value: 'json', label: 'JSON', icon: Code, description: 'Structured JSON data matching expected output format' },
        { value: 'text', label: 'Text', icon: Type, description: 'Plain text to be processed into JSON' }
      ],
      'XML': [
        { value: 'text', label: 'Text', icon: Type, description: 'Plain text or XML content for processing' },
        { value: 'json', label: 'JSON', icon: Code, description: 'JSON data to be converted to XML' }
      ],
      'Structured Text': [
        { value: 'text', label: 'Text', icon: Type, description: 'Raw text content for structuring' },
        { value: 'json', label: 'JSON', icon: Code, description: 'JSON data to be formatted as structured text' }
      ],
      'CSV': [
        { value: 'csv', label: 'CSV', icon: Table, description: 'CSV data for processing and transformation' },
        { value: 'text', label: 'Text', icon: Type, description: 'Text content to be converted to CSV format' },
        { value: 'json', label: 'JSON', icon: Code, description: 'JSON data to be converted to CSV' }
      ],
      'Formatted Text': [
        { value: 'text', label: 'Text', icon: Type, description: 'Raw text content for formatting' },
        { value: 'json', label: 'JSON', icon: Code, description: 'JSON data to be formatted as text' }
      ]
    };

    const baseTypes = inputTypeConfig[outputFormat as keyof typeof inputTypeConfig] || [
      { value: 'json', label: 'JSON', icon: Code, description: 'JSON data for processing' },
      { value: 'text', label: 'Text', icon: Type, description: 'Text content for processing' }
    ];

    // Always add file option
    return [
      ...baseTypes,
      { value: 'file', label: 'File', icon: FileUp, description: 'Upload files in any supported format' }
    ];
  };

  const availableInputTypes = getAvailableInputTypes(formData.outputFormat);

  // Auto-detect input type based on output format, but only if current type is not available
  useEffect(() => {
    const availableTypeValues = availableInputTypes.map(type => type.value);

    if (!availableTypeValues.includes(testInputType)) {
      // Current input type is not available for the selected output format
      const formatToDefaultInput = {
        'JSON': 'json',
        'XML': 'text',
        'Structured Text': 'text',
        'CSV': 'csv',
        'Formatted Text': 'text'
      };

      const defaultType = formatToDefaultInput[formData.outputFormat as keyof typeof formatToDefaultInput] || 'json';
      setTestInputType(defaultType as any);
    }
  }, [formData.outputFormat, availableInputTypes, testInputType]);

  // Auto-populate custom output example when format changes if it's empty
  // useEffect(() => {
  //   if (!formData.customOutputExample && formData.outputFormat) {
  //     const defaultExample = getOutputFormatDetails(formData.outputFormat).example;
  //     setFormData(prev => ({ ...prev, customOutputExample: defaultExample }));
  //   }
  // }, [formData.outputFormat, formData.customOutputExample]);

  // Update form data when selected prompt changes
  // useEffect(() => {
  //   if (selectedPromptData) {
  //     setFormData({
  //       name: selectedPromptData.name || '',
  //       description: selectedPromptData.description || '',
  //       category: selectedPromptData.category || '',
  //       status: selectedPromptData.status || 'Draft',
  //       systemPrompt: selectedPromptData.systemPrompt || '',
  //       promptText: selectedPromptData.promptText || '',
  //       linkedDocumentType: selectedPromptData.linked_document_type || '',
  //       outputFormat: selectedPromptData.outputFormat || 'JSON',
  //       targetModel: selectedPromptData.targetModel || 'GPT-4',
  //       temperature: selectedPromptData.temperature || 0.7,
  //       maxTokens: selectedPromptData.maxTokens || 1000,
  //       conversationRoles: selectedPromptData.conversationRoles || [
  //         {
  //           id: 'system',
  //           role: 'System',
  //           content: '',
  //           isCollapsed: false
  //         },
  //         {
  //           id: 'user',
  //           role: 'User',
  //           content: '',
  //           isCollapsed: false
  //         }
  //       ],
  //       tasks: selectedPromptData.tasks || [''],
  //       instructions: selectedPromptData.instructions || [''],
  //       customOutputExample: selectedPromptData.customOutputExample || ''
  //     });
  //   }
  // }, [selectedPromptData]);

  const resolveThinkingBudget = (): number | 'empty' | null => {
    if (!isBudgetNumeric) return null; // budget not applicable for this model

    const raw = thinkingBudgetInput.trim();
    if (raw === '') return formData?.enableThinking ? 'empty' : null;

    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) return null;

    if (formData?.enableThinking && (n < modelBudgetMin || n > modelBudgetMax)) return null;

    return Math.max(modelBudgetMin, Math.min(modelBudgetMax, n));
  };

  const createPromtsApi = async () => {
    if (formData?.enableThinking && isBudgetNumeric) {
      const budgetCheck = resolveThinkingBudget();
      if (budgetCheck === 'empty') {
        toast.error('Please enter a thinking budget');
        return;
      }
      if (budgetCheck === null) {
        toast.error(`Please enter a value between ${modelBudgetMin.toLocaleString()} and ${modelBudgetMax.toLocaleString()}`);
        return;
      }
    }
    const budgetResolved = isBudgetNumeric ? resolveThinkingBudget() : undefined;
    const budget = (budgetResolved === 'empty' || budgetResolved === null) ? undefined : budgetResolved;
    const conversation_roles = await formData?.conversationRoles?.map(({ id, ...rest }) => rest);
    const dataToSend: Record<string, any> =
    {
      name: formData?.name,
      description: formData?.description,
      category: formData?.category,
      model_type: formData?.targetModel,
      temperature: formData?.temperature ?? 0,
      enable_thinking: formData?.enableThinking ?? false,
      ...(normalizedThinkingLevels && formData?.thinkingLevel ? { thinking_level: formData.thinkingLevel } : {}),
      ...(budget !== undefined ? { thinking_budget: budget } : {}),
      snapshot: {
        model_name: formData?.targetModel,
        conversation_roles: conversation_roles,
        parameters: {
          temperature: formData?.temperature,
          max_tokens: formData?.maxTokens
        },
        tasks: formData?.tasks,
        instructions: formData?.instructions
      },
      linked_document_type: formData?.linkedDocumentType,
      output_format: formData?.outputFormat,
      status: formData?.status,
      output_template: formData?.customOutputExample||'',
      activate: false

    }

    console.log(dataToSend, "dataToSend", formData)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/prompts`, {


        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }
      
      toast.success(data?.message || "Prompt created successfully");

      // const newData = await generateMockHarvestData(data)
      return data;
    } catch (error: any) {
      toast.error(error.message||'Request failed');
      console.error('Error:', error.message||'Request failed');

      return null;
    } finally {
      setCurrentView('ai-prompt-manager');
      navigate(ROUTES.promptsList)
    }

  }

  const handleSaveInitiate = () => {

    console.log(formData, "formData")
    if (isNewPrompt) {
      // For new prompts, show save dialog with create options
      setShowSaveDialog(true);
    } else {
      // For existing prompts, show version save dialog
      setShowSaveDialog(true);
    }
  };

  const handleCreatePrompt = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a prompt name');
      return;
    }

    setShowSaveDialog(false);
    setIsSaving(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Create the new prompt data
    const newPromptData = {
      ...promptData,
      ...formData,
      id: `prompt-${Date.now()}`,
      isNew: false, // Remove the new flag
      usageCount: 0,
      successRate: 0,
      avgResponseTime: 0,
      lastUpdated: new Date(),
      author: 'Current User'
    };

    // Update the selected prompt data
    setSelectedPromptData(newPromptData);

    // Add to version history
    const newHistoryItem: VersionHistoryItem = {
      version: '1.0',
      date: new Date(),
      author: 'Current User',
      changes: 'Initial version created'
    };

    setVersionHistory([newHistoryItem]);

    setIsSaving(false);
    setIsEditing(false);
    toast.success('Prompt created successfully');
  };

  const handleUpdateCurrent = async () => {
    setShowSaveDialog(false);
    setIsSaving(true);

    // Simulate API call
    // await new Promise(resolve => setTimeout(resolve, 1000));


    // const dataToSend =
    // {
    //   name: formData?.name,
    //   description: formData?.description,
    //   category: "Clinical",
    //   snapshot: {
    //     model_name: formData?.targetModel,
    //     system_prompt: formData?.conversationRoles[0].content,
    //     parameters: {
    //       temperature: formData?.temperature,
    //       max_tokens: formData?.maxTokens
    //     }
    //   },
    //   linked_document_type: formData?.linkedDocumentType,
    //   output_format: formData?.outputFormat,
    //   status: "DRAFT",
    //   activate: false,
    //   versionId: selectedPromptData?.version_id

    // }

    if (formData?.enableThinking && isBudgetNumeric) {
      const budgetCheck = resolveThinkingBudget();
      if (budgetCheck === 'empty') {
        toast.error('Please enter a thinking budget');
        setIsSaving(false);
        return;
      }
      if (budgetCheck === null) {
        toast.error(`Please enter a value between ${modelBudgetMin.toLocaleString()} and ${modelBudgetMax.toLocaleString()}`);
        setIsSaving(false);
        return;
      }
    }
    const budgetResolved = isBudgetNumeric ? resolveThinkingBudget() : undefined;
    const budget = (budgetResolved === 'empty' || budgetResolved === null) ? undefined : budgetResolved;
    const newDataToSend: Record<string, any> = {
      name: formData?.name,
      description: formData?.description,
      category: formData?.category,
      model_type: formData?.targetModel,
      temperature: formData?.temperature ?? 0,
      enable_thinking: formData?.enableThinking ?? false,
      ...(normalizedThinkingLevels && formData?.thinkingLevel ? { thinking_level: formData.thinkingLevel } : {}),
      ...(budget !== undefined ? { thinking_budget: budget } : {}),
      snapshot: {
        model_name: formData?.targetModel,
        conversation_roles: formData?.conversationRoles,
        parameters: {
          temperature: formData?.temperature,
          max_tokens: formData?.maxTokens
        },
      },
      linked_document_type: formData?.linkedDocumentType,
      output_format: formData?.outputFormat,
      status: formData?.status,
      output_template: formData?.customOutputExample||'',
      activate: false,
      versionId: selectedPromptData?.version_id || passedRunData?.version_id
    }
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/prompts/${selectedPromptData?.prompt_id || passedRunData?.prompt_id}`, {


        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newDataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message|| 'Request failed');
      }
      // const newData = await generateMockHarvestData(data)
      toast.success(data?.message || "Prompt updated  successfully");

      return data;
    } catch (error: any) {
toast.error(error.message)
      return null;
    } finally {
      setIsSaving(false);
      setIsEditing(false);
      await fetchData();
      await fetchPromtVersion()
    }

    // Update the selected prompt data
    setSelectedPromptData({
      ...promptData,
      ...formData
    });

    // Add to version history as an update
    const newHistoryItem: VersionHistoryItem = {
      version: promptData.version || '1.0',
      date: new Date(),
      author: 'Current User', // This would come from auth context
      changes: 'Updated prompt configuration and content'
    };

    setVersionHistory(prev => [newHistoryItem, ...prev.slice(1)]); // Replace the latest version

    setIsSaving(false);
    setIsEditing(false);
    toast.success('Prompt updated successfully');
  };

  const handleUpdateNewVersion = async () => {
    setShowSaveDialog(false);
    setIsSaving(true);

    if (formData?.enableThinking && isBudgetNumeric) {
      const budgetCheck = resolveThinkingBudget();
      if (budgetCheck === 'empty') {
        toast.error('Please enter a thinking budget');
        setIsSaving(false);
        return;
      }
      if (budgetCheck === null) {
        toast.error(`Please enter a value between ${modelBudgetMin.toLocaleString()} and ${modelBudgetMax.toLocaleString()}`);
        setIsSaving(false);
        return;
      }
    }
    const budgetResolved = isBudgetNumeric ? resolveThinkingBudget() : undefined;
    const budget = (budgetResolved === 'empty' || budgetResolved === null) ? undefined : budgetResolved;
    const newDataToSend: Record<string, any> = {
      name: formData?.name,
      description: formData?.description,
      category: formData?.category,
      model_type: formData?.targetModel,
      temperature: formData?.temperature ?? 0,
      enable_thinking: formData?.enableThinking ?? false,
      ...(normalizedThinkingLevels && formData?.thinkingLevel ? { thinking_level: formData.thinkingLevel } : {}),
      ...(budget !== undefined ? { thinking_budget: budget } : {}),
      snapshot: {
        model_name: formData?.targetModel,
        conversation_roles: formData?.conversationRoles,
        parameters: {
          temperature: formData?.temperature,
          max_tokens: formData?.maxTokens
        },
      },
      linked_document_type: formData?.linkedDocumentType,
      output_format: formData?.outputFormat,
      status: "DRAFT",
      output_template: formData?.customOutputExample,
      activate: false,
    }
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/prompts/${selectedPromptData?.prompt_id || passedRunData?.prompt_id}`, {


        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newDataToSend)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Request failed');
      }
      // const newData = await generateMockHarvestData(data)
      toast.success(data?.message || "Prompt saved as new version");

      return data;
    } catch (error: any) {
toast.error(error.message)
      return null;
    } finally {
      setIsSaving(false);
      setIsEditing(false);
       await fetchData();
      await fetchPromtVersion();
    }
  };

  const handleSaveAsNewVersion = async () => {
    setShowSaveDialog(false);
    setIsSaving(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Create new version number
    const currentVersion = promptData.version || '1.0';
    const versionParts = currentVersion.split('.');
    const majorVersion = parseInt(versionParts[0]);
    const minorVersion = parseInt(versionParts[1] || '0');
    const newVersion = `${majorVersion}.${minorVersion + 1}`;

    // Update the selected prompt data with new version
    setSelectedPromptData({
      ...promptData,
      ...formData,
      version: newVersion
    });

    // Add to version history as a new version
    const newHistoryItem: VersionHistoryItem = {
      version: newVersion,
      date: new Date(),
      author: 'Current User', // This would come from auth context
      changes: 'Created new version with updated configuration and content'
    };

    setVersionHistory(prev => [newHistoryItem, ...prev]);

    setIsSaving(false);
    setIsEditing(false);
    toast.success(`New version ${newVersion} created successfully`);
  };

  const handleTest = async () => {
    setIsTesting(true);

    // Simulate API test call
    await new Promise(resolve => setTimeout(resolve, 2000));

    const mockResult: TestResult = {
      id: Date.now().toString(),
      timestamp: new Date(),
      success: Math.random() > 0.2, // 80% success rate
      responseTime: Math.random() * 3000 + 500,
      output: `{
  "customer_name": "John Doe",
  "contact_details": {
    "phone": "+1-555-0123",
    "email": "john.doe@example.com"
  },
  "company": "Acme Corp",
  "confidence_score": 0.95
}`,
      model: formData.targetModel
    };

    setTestResults(prev => [mockResult, ...prev].slice(0, 10));
    setIsTesting(false);

    if (mockResult.success) {
      toast.success('Test completed successfully');
    } else {
      toast.error('Test failed - check configuration');
    }
  };

  const handleDelete = () => {
    setCurrentView('ai-prompt-manager');
    toast.success('Prompt deleted successfully');
  };

  const handleDeletePrompt = async (versionId: any) => {

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}prompts/versions/${versionId}`, {
        method: "DELETE",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      })
      const data = await response?.json();
       if (!response.ok) {
         toast.error(data?.message || data?.error || "Request failed");
         return;
       }
       await fetchData()
      toast.success(data?.message || "Prompt archived successfully");
     } catch (error) {
       if (error instanceof Error) {
        toast.error(error.message);
       } else {
         toast.error("Something went wrong");
       }
     }

    // toast.success('Prompt deleted successfully');
    setCurrentView('ai-prompt-manager');
    navigate(ROUTES.promptsList)
  };

  const handleDuplicate = () => {
    // Generate date suffix in _ddmmyyyy format
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0'); // getMonth() returns 0-11
    const year = today.getFullYear();
    const dateSuffix = `_${day}${month}${year}`;

    // Deep copy conversation roles with new IDs to avoid conflicts
    const copiedConversationRoles = formData.conversationRoles.map((role: any) => ({
      ...role,
      id: `role-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` // Generate new unique ID
    }));

    const duplicatedPrompt = {
      // Start with base prompt data
      ...promptData,
      // Override with current form data to ensure we get the latest edits
      ...formData,
      // Set new identification and metadata
      name: `${formData.name || promptData.name}${dateSuffix}`,
      id: `${promptData.id}-copy-${Date.now()}`,
      isNew: true,
      // Reset usage stats for new prompt
      usageCount: 0,
      successRate: 0,
      avgResponseTime: 0,
      // Copy all prompt details from current form state with proper deep copying
      conversationRoles: copiedConversationRoles,
      tasks: [...formData.tasks], // Create new array to avoid reference issues
      instructions: [...formData.instructions], // Create new array to avoid reference issues
      // Preserve other configuration from form data
      systemPrompt: formData.systemPrompt,
      promptText: formData.promptText,
      linkedDocumentType: formData.linkedDocumentType,
      outputFormat: formData.outputFormat,
      targetModel: formData.targetModel,
      enableThinking: formData.enableThinking ?? false,
      temperature: formData.temperature,
      maxTokens: formData.maxTokens,
      category: formData.category,
      status: 'Draft', // Set status to Draft for duplicated prompts
      customOutputExample: formData.customOutputExample // Include custom output example
    };

    // setSelectedPromptData(duplicatedPrompt);
    setFormData(duplicatedPrompt);
    setIsEditing(true);
    toast.success(`Prompt duplicated with suffix ${dateSuffix}. All details including conversation roles, tasks, and instructions have been copied.`);
  };

  const handleCancel = () => {
    if (isNewPrompt) {
      // If canceling creation of a new prompt, go back to prompt manager
      setCurrentView('ai-prompt-manager');
      navigate(ROUTES.promptsList)
    } else {
      // If canceling edit of existing prompt, just exit edit mode
      setIsEditing(false);
      // Reset form data to original values
      setFormData({
        name: promptData.name || '',
        description: promptData.description || '',
        category: promptData.category || '',
        status: promptData.status || 'Draft',
        systemPrompt: promptData.systemPrompt || '',
        promptText: promptData.promptText || '',
        linkedDocumentType: promptData.linkedDocumentType || '',
        outputFormat: promptData.outputFormat || 'JSON',
        targetModel: promptData.targetModel || '',
        enableThinking: promptData.enableThinking ?? false,
        temperature: promptData.temperature || 0.1,
        maxTokens: promptData.maxTokens || 5000,
        conversationRoles: promptData.conversationRoles || [],
        tasks: promptData.tasks || [],
        instructions: promptData.instructions || [],
        customOutputExample: promptData.customOutputExample || ''
      });
    }
  };

  // Conversation Roles functions
  const addRole = () => {
    const newRole: ConversationRole = {
      id: `role-${Date.now()}`,
      type: 'Assistant',
      content: '',
      isCollapsed: false
    };
    setFormData(prev => ({
      ...prev,
      conversationRoles: [...prev?.conversationRoles, newRole]
    }));
  };

  const updateRole = (id: string, field: keyof ConversationRole, value: any) => {
    setFormData(prev => ({
      ...prev,
      conversationRoles: prev.conversationRoles.map((role: any) =>
        role.id === id ? { ...role, [field]: value } : role
      )
    }));
  };

  const deleteRole = (id: string) => {
    setFormData(prev => ({
      ...prev,
      conversationRoles: prev.conversationRoles.filter((role: any) => role.id !== id)
    }));
  };

  // Tasks functions
  const addTask = () => {
    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, '']
    }));
  };

  const updateTask = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map((task: any, i: any) => i === index ? value : task)
    }));
  };

  const deleteTask = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_: any, i: any) => i !== index)
    }));
  };

  // Instructions functions
  const addInstruction = () => {
    setFormData(prev => ({
      ...prev,
      instructions: [...prev.instructions, '']
    }));
  };

  const updateInstruction = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      instructions: prev.instructions.map((instruction: any, i: any) => i === index ? value : instruction)
    }));
  };

  const deleteInstruction = (index: number) => {
    setFormData(prev => ({
      ...prev,
      instructions: prev.instructions.filter((_: any, i: any) => i !== index)
    }));
  };

  // File handling
  const handleFileSelect = (file: File) => {
    setTestInputs(prev => ({ ...prev, file }));
    toast.success(`File "${file.name}" selected`);
  };

  console.log(formData?.category,"formData?.category")
  const getJsonDetails= (jsonString: any) => {
    let jsonstring;
    if(jsonString?.output_template && typeof jsonString?.output_template === 'string'){
      jsonstring=cleanJSONCodeFn(jsonString?.output_template);
      jsonstring=JSON.parse(jsonstring);
    }
    else{
      jsonstring=jsonString?.output_template

    }
    let fieldCount = 0;
  let maxDepth = 1;
  let arrayCount = 0;

  function traverse(obj, depth) {
    if (Array.isArray(obj)) {
      arrayCount++;
      obj.forEach(item => traverse(item, depth + 1));
    } else if (obj && typeof obj === 'object') {
      fieldCount += Object.keys(obj).length;
      maxDepth = Math.max(maxDepth, depth);
      Object.values(obj).forEach(value => traverse(value, depth + 1));
    }
  }

  traverse(jsonstring, 1);
return {
      numberOfFields: fieldCount,
      numberOfArray: arrayCount,
      nestedObjects: maxDepth - 1 // Subtract 1 to get the number of nested levels
    };
      // return reslutDetail;
  }
  function analyzeJSON(versionKey:string,json:any) {
    const stats = getJsonDetails(json); // same helper from before
  setJsonStats(prev => ({
    ...prev,
    [versionKey]: {
      numberOfFields: stats?.numberOfFields,
      numberOfArray: stats?.numberOfArray,
      nestedObjects: stats?.nestedObjects
    }
  }));
}

const copyToClipboard =async (content: string) => {
  try {
    if(!content) throw new Error("Failed to copy text to clipboard");
    // const jsonString = JSON.stringify(content, null, 2);
      await navigator.clipboard.writeText(content);
      // showCopyFeedback(buttonId);
      toast.success('Output copied to clipboard', {
        description: `${content.length} characters copied successfully`
      });
    } 
  catch (err) {
      // console.error('Failed to copy text: ', err);
      toast.error('Failed to copy text to clipboard');
    
  }
}

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white">
        <div className="p-8">
          {/* Breadcrumb Navigation */}
          <div className="mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                {/* <BreadcrumbItem>
                  <BreadcrumbLink href="#" className="flex items-center gap-2">
                    <Home className="w-3 h-3" />
                    Dashboard
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator /> */}
                <BreadcrumbItem>
                  <BreadcrumbLink
                    href="#"
                    onClick={() => {
                      setCurrentView('ai-prompt-manager');
                      navigate(ROUTES.promptsList);
                    }}
                    className="flex items-center gap-2"
                  >
                    <Brain className="w-3 h-3" />
                    AI Prompt Manager
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    {isNewPrompt ? 'Create Prompt' : 'Prompt Details'}
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCurrentView('ai-prompt-manager');
                    navigate(ROUTES.promptsList);
                  }}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Prompts
                </Button>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="bg-blue-100 rounded-lg p-2">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                    </div>
                    <h1 className="text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text">
                      {isNewPrompt ? 'Create New Prompt' : (formData?.name || promptData?.name)}
                    </h1>
                    {!isNewPrompt && (
                      <Badge className={getStatusColor(promptData?.status)}>
                        {promptData?.status}
                      </Badge>
                    )}
                    {isNewPrompt && (
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                        New
                      </Badge>
                    )}
                  </div>
                  <p className="text-gray-600">
                    {isNewPrompt
                      ? 'Configure your new AI prompt with comprehensive settings and testing capabilities'
                      : (formData?.description || promptData?.description)
                    }
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {!isEditing ? (
                  <>
                  {hasPermission("prompts:edit") && (
                    <Button
                      variant="outline"
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </Button>)}
                    {/* {hasPermission("prompts:create") && (
                    <Button
                      variant="outline"
                      onClick={ () => handleDuplicate() }
                      className="flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Duplicate
                    </Button>)} */}
                    {hasPermission("prompts:archive") && !isNewPrompt && (
                      <Button
                        variant="destructive"
                        onClick={() => setShowDeleteDialog(true)}
                        className="flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Archive
                      </Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      className="flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveInitiate}
                      disabled={isSaving}
                      className="flex items-center gap-2"
                    >
                      <Save className="w-4 h-4" />
                      {isSaving ? 'Saving...' : (isNewPrompt ? 'Create Prompt' : 'Save Changes')}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Quick Stats - Only show for existing prompts */}
            {!isNewPrompt && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-900">{promptData?.successRate?.toFixed(1)}%</p>
                        <p className="text-gray-600 text-sm">Success Rate</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-900">{promptData?.avgResponseTime?.toFixed(1)}s</p>
                        <p className="text-gray-600 text-sm">Avg Response</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-purple-100 p-2 rounded-lg">
                        <Activity className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-900">{promptData?.usageCount}</p>
                        <p className="text-gray-600 text-sm">Total Uses</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-100 p-2 rounded-lg">
                        <GitBranch className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-900">{promptData?.version}</p>
                        <p className="text-gray-600 text-sm">Version</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-indigo-100 p-2 rounded-lg">
                        <Target className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-xl font-bold text-gray-900 truncate" title={models.find((m) => m.id === formData?.targetModel)?.name ?? formData?.targetModel}>
                          {models.find((m) => m.id === formData?.targetModel)?.name ?? formData?.targetModel ?? '—'}
                        </p>
                        <p className="text-gray-600 text-sm">Model</p>
                      </div>
                    </div>
                  </CardContent>
                </Card> */}
              </div>
            )}
          </div>

          {/* Main Content */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Basic Info</TabsTrigger>
              <TabsTrigger value="prompt">Prompt</TabsTrigger>
              {/* <TabsTrigger value="testing">Test & Preview</TabsTrigger> */}
              <TabsTrigger value="history" disabled={isNewPrompt}>History</TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-6">
              <div className="grid grid-cols-1  gap-6">
                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Basic Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Prompt Name</Label>
                      <Input
                        id="name"
                        ref={nameInputRef}
                        value={formData?.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        // disabled={!isNewPrompt}
                         disabled={!isEditing}
                        placeholder="Enter prompt name"
                        className={isNewPrompt && touched && !formData.name ? "border-red-300" : ""}
                        onBlur={() => setTouched(true)} 
                      />
                      {isNewPrompt && touched && !formData?.name && (
                        <p className="text-sm text-red-600">Prompt name is required</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData?.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        disabled={!isEditing}
                        placeholder="Enter prompt description"
                        rows={3}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select
                          value={formData?.category}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                          // Category is only set on create; it is immutable on edit
                          disabled={!isNewPrompt}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {category?.map((category:any) => (
                              <SelectItem key={category} value={category}>
                                {category}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2 disable">
                        <Label htmlFor="status">Status</Label>
                        <Select
                          value={formData?.status?.toUpperCase()}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                          disabled={!isEditing}
                        // disabled={true}

                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="DRAFT">Draft</SelectItem>
                            <SelectItem value="TEST">Test</SelectItem>
                            <SelectItem value="ACTIVE">Active</SelectItem>
                            <SelectItem value="ARCHIVED">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="targetModel">Model</Label>
                        <Select
                          value={formData?.targetModel}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, targetModel: value }))}
                          disabled={!isEditing}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select model" />
                          </SelectTrigger>
                          <SelectContent>
                            {models.map((model) => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {models.length === 0 && (
                          <p className="text-sm text-muted-foreground">Loading models…</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="temperature">Temperature</Label>
                        <div className="flex items-center gap-3">
                          <Slider
                            id="temperature"
                            min={0}
                            max={1}
                            step={0.1}
                            value={[typeof formData?.temperature === 'number' ? formData.temperature : parseFloat(String(formData?.temperature ?? 0)) || 0]}
                            onValueChange={(value) => setFormData((prev) => ({ ...prev, temperature: value[0] ?? 0 }))}
                            disabled={!isEditing}
                            className="flex-1"
                          />
                          <span className="text-sm text-muted-foreground w-8 tabular-nums">
                            {typeof formData?.temperature === 'number' ? formData.temperature : parseFloat(String(formData?.temperature ?? 0)) || 0}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">0 = deterministic, 1 = more variation</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="flex flex-col justify-end space-y-2">
                        <Label>Thinking Mode</Label>
                        <div className="flex items-center gap-2 h-10">
                          <Switch
                            id="enableThinking"
                            checked={formData?.enableThinking ?? false}
                            onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, enableThinking: !!checked }))}
                            disabled={thinkingUiLocked || !modelThinkingModeEnabled}
                          />
                          <span className="text-sm text-muted-foreground">
                            {formData?.enableThinking ? 'On' : 'Off'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {formData?.enableThinking && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        <div className="space-y-2">
                          <Label htmlFor="thinkingLevel">Thinking Level</Label>
                          <Select
                            value={formData?.thinkingLevel || ''}
                            onValueChange={(value) =>
                              setFormData((prev) => ({ ...prev, thinkingLevel: value }))
                            }
                            disabled={thinkingUiLocked || !normalizedThinkingLevels}
                          >
                            <SelectTrigger id="thinkingLevel">
                              <SelectValue placeholder={normalizedThinkingLevels ? 'Select level' : 'N/A'} />
                            </SelectTrigger>
                            <SelectContent>
                              {(normalizedThinkingLevels ?? []).map((lvl) => (
                                <SelectItem key={lvl} value={lvl}>
                                  {lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="thinkingBudget">Thinking Budget</Label>
                          <Input
                            id="thinkingBudget"
                            type="text"
                            inputMode="numeric"
                            placeholder={isBudgetNumeric ? 'Enter a budget value' : 'N/A'}
                            value={thinkingBudgetInput}
                            onChange={(e) => {
                              setThinkingBudgetError(null);
                              const raw = e.target.value.replace(/\D/g, '');
                              setThinkingBudgetInput(raw);
                            }}
                            onBlur={() => {
                              const raw = thinkingBudgetInput.trim();
                              if (raw === '') {
                                setFormData((prev) => ({ ...prev, thinkingBudget: modelBudgetMin }));
                                setThinkingBudgetInput(String(modelBudgetMin));
                                setThinkingBudgetError(null);
                                return;
                              }
                              const num = parseInt(raw, 10);
                              if (Number.isNaN(num)) {
                                setThinkingBudgetError('Enter a valid number');
                                setThinkingBudgetInput(String(formData?.thinkingBudget ?? 1000));
                                return;
                              }
                              if (isBudgetNumeric && (num < modelBudgetMin || num > modelBudgetMax)) {
                                // Keep the invalid text visible so submit-time validation blocks the API call.
                                setThinkingBudgetError(`Please enter a value between ${modelBudgetMin.toLocaleString()} and ${modelBudgetMax.toLocaleString()}`);
                                return;
                              }
                              // For N/A budgets, fall back to backend-safe defaults.
                              const nextBudget = isBudgetNumeric
                                ? num
                                : Math.max(BUDGET_MIN, Math.min(BUDGET_MAX, num));
                              setFormData((prev) => ({ ...prev, thinkingBudget: nextBudget }));
                              setThinkingBudgetInput(String(num));
                              setThinkingBudgetError(null);
                            }}
                            disabled={thinkingUiLocked || !modelThinkingModeEnabled || !isBudgetNumeric}
                            className={thinkingBudgetError ? 'border-destructive' : ''}
                          />
                          {thinkingBudgetError ? (
                            <p className="text-xs text-destructive">{thinkingBudgetError}</p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Range: {isBudgetNumeric ? `${modelBudgetMin.toLocaleString()} – ${modelBudgetMax.toLocaleString()}` : 'N/A'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Configuration
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="targetModel">Target Model *</Label>
                      <Select
                        value={formData?.targetModel}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, targetModel: value }))}
                        disabled={!isEditing}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select target model" />
                        </SelectTrigger>
                        <SelectContent>
                          {/*<SelectItem value="GPT-4">GPT-4</SelectItem>
                           <SelectItem value="GPT-4 Turbo">GPT-4 Turbo</SelectItem>
                          <SelectItem value="GPT-3.5 Turbo">GPT-3.5 Turbo</SelectItem>
                          <SelectItem value="Claude 3 Opus">Claude 3 Opus</SelectItem>
                          <SelectItem value="Claude 3 Sonnet">Claude 3 Sonnet</SelectItem>
                          <SelectItem value="Claude 3 Haiku">Claude 3 Haiku</SelectItem>
                          <SelectItem value="Gemini Pro">Gemini Pro</SelectItem>
                          <SelectItem value="Gemini Ultra">Gemini Ultra</SelectItem>
                          <SelectItem value="PaLM 2">PaLM 2</SelectItem>
                          <SelectItem value="LLaMA 2">LLaMA 2</SelectItem>
                          <SelectItem value="Mixtral 8x7B">Mixtral 8x7B</SelectItem>
                          <SelectItem value="Anthropic Claude">Anthropic Claude</SelectItem>
                          <SelectItem value="Cohere Command">Cohere Command</SelectItem>
                          <SelectItem value="AI21 Jurassic">AI21 Jurassic</SelectItem>
                          <SelectItem value="Custom Model">Custom Model</SelectItem> /}
                          <SelectItem value="Gemini Flash 2.0">Gemini Flash 2.0</SelectItem>
                          {/* <SelectItem value="Gemini Flash 2.5">Gemini Flash 2.5</SelectItem> /}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="linkedDocument">Linked Document Type</Label>
                        <Select
                          value={formData?.linkedDocumentType}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, linkedDocumentType: value }))}
                          disabled={!isEditing}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select document type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ClinicalNote">Clinical Notes</SelectItem>
                            {/* <SelectItem value="LabResults">Lab Results</SelectItem>
                            <SelectItem value="ImagingReports">Imaging Reports</SelectItem>
                            <SelectItem value="Patient Records">Patient Records</SelectItem>
                            <SelectItem value="Treatment Plans">Treatment Plans</SelectItem> /}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="outputFormat">Output Format</Label>
                        <Select
                          value={formData?.outputFormat}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, outputFormat: value }))}
                          disabled={!isEditing}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select output format" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="JSON">JSON</SelectItem>
                            {/* <SelectItem value="XML">XML</SelectItem>
                            <SelectItem value="Structured Text">Structured Text</SelectItem>
                            <SelectItem value="CSV">CSV</SelectItem>
                            <SelectItem value="Formatted Text">Formatted Text</SelectItem> /}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="temperature">Temperature</Label>
                        <Input
                          id="temperature"
                          type="number"
                          min="0"
                          max="2"
                          step="0.1"
                          value={formData?.temperature}
                          onChange={(e) => setFormData(prev => ({ ...prev, temperature: parseFloat(e.target.value) || 0 }))}
                          disabled={!isEditing}
                          placeholder="0.1"
                        />
                        <p className="text-xs text-gray-500">Controls randomness (0.0 = deterministic, 2.0 = very random)</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="maxTokens">Max Tokens</Label>
                        <Input
                          id="maxTokens"
                          type="number"
                          min="1"
                          max="4000"
                          value={formData?.maxTokens}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData(prev => ({
                              ...prev,
                              maxTokens: value === '' ? '' : parseInt(value)
                            }));
                          }}
                          disabled={!isEditing}
                          placeholder="5000"
                        />
                        <p className="text-xs text-gray-500">Maximum length of the response</p>
                      </div>
                    </div>
                  </CardContent>
                </Card> */}
              </div>
            </TabsContent>

            {/* Enhanced Prompt Tab with Output Format Section */}
            <TabsContent value="prompt" className="space-y-6">
              {/* Conversation Roles */}
              <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Conversation Prompt
                  </CardTitle>
                  <div className="flex justify-between gap-2">
                  <CardDescription>
                    Define the conversation structure between different roles and the specific tasks to be performed. Provide specific instructions and guidelines
                  </CardDescription>
                  <div>
                   { formData.conversationRoles[0].content && <Copy className="w-4 h-4 mr-2 inline cursor-pointer"  onClick={() => copyToClipboard(formData.conversationRoles[0].content)}/>}
                  </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {Array.isArray(formData?.conversationRoles) && formData.conversationRoles.length > 0 && (
                    <div
                      key={formData.conversationRoles[0].id}
                      className="border rounded-lg p-4 space-y-3 bg-gray-50"
                    >
                      {!formData.conversationRoles[0].isCollapsed && (
                        <Textarea
                          value={formData.conversationRoles[0].content}
                          onChange={(e) =>
                            updateRole(formData.conversationRoles[0].id, 'content', e.target.value)
                          }
                          disabled={!isEditing}
                          placeholder={`Enter prompt content...`}
                          rows={4}
                          className="font-mono text-sm h-24 h-[50vh] focus:h-[60vh] transition-all duration-300 ease-in-out"
                        />
                      )}
                    </div>
                  )}
                  {/* {formData?.conversationRoles?.map((role: any) => (
                    <div key={role.id} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateRole(role.id, 'isCollapsed', !role.isCollapsed)}
                            className="p-1 h-6 w-6"
                          >
                            {role.isCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                          </Button>
                          <Select
                            value={role?.type?.toLowerCase() || role?.role?.toLowerCase()}
                            onValueChange={(value) => updateRole(role.id, 'type', value)}
                            disabled={!isEditing}
                          >
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="system">System</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="assistant">Assistant</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {isEditing && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteRole(role.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>

                      {!role.isCollapsed && (
                        <Textarea
                          value={role.content}
                          onChange={(e) => updateRole(role.id, 'content', e.target.value)}
                          disabled={!isEditing}
                          placeholder={`Enter ${role?.role?.toLowerCase()} prompt content...`}
                          rows={4}
                          className="font-mono text-sm h-24 h-[50vh] focus:h-[60vh] transition-all duration-300 ease-in-out "
                        />
                      )}
                    </div>
                  ))} */}
                  
                 {isEditing && (!Array.isArray(formData?.conversationRoles) || formData.conversationRoles.length === 0) && (
                    <Button
                      variant="outline"
                      onClick={addRole}
                      className="flex items-center gap-2 w-full"
                    >
                      <Plus className="w-4 h-4" />
                      Add Role
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Tasks, Instructions, and Output Format Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">

                {/* <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ListChecks className="w-4 h-4" />
                      Tasks
                    </CardTitle>
                    <CardDescription>
                      Define the specific tasks to be performed
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {formData?.tasks?.map((task: any, index: any) => (
                      <div key={index} className="flex items-start gap-2 p-3 border rounded-lg bg-gray-50">
                        <Textarea
                          value={task}
                          onChange={(e) => updateTask(index, e.target.value)}
                          disabled={!isEditing}
                          placeholder="Enter task description..."
                          rows={2}
                          className="flex-1 text-sm  h-24 focus:h-40 transition-all duration-300 ease-in-out "
                        // className="flex-1 text-sm "
                        />
                        {isEditing && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteTask(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}

                    {isEditing && (
                      <Button
                        variant="outline"
                        onClick={addTask}
                        className="flex items-center gap-2 w-full"
                      >
                        <Plus className="w-4 h-4" />
                        Add Task
                      </Button>
                    )}
                  </CardContent>
                </Card>

               
                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      Instructions
                    </CardTitle>
                    <CardDescription>
                      Provide specific instructions and guidelines
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {formData?.instructions?.map((instruction: any, index: any) => (
                      <div key={index} className="flex items-start gap-2 p-3 border rounded-lg bg-gray-50">
                        <Textarea
                          value={instruction}
                          onChange={(e) => updateInstruction(index, e.target.value)}
                          disabled={!isEditing}
                          placeholder="Enter instruction..."
                          rows={2}
                          // className="flex-1 text-sm"
                          className="flex-1 text-sm  h-24 focus:h-40 transition-all duration-300 ease-in-out "

                        />
                        {isEditing && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteInstruction(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 mt-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}

                    {isEditing && (
                      <Button
                        variant="outline"
                        onClick={addInstruction}
                        className="flex items-center gap-2 w-full"
                      >
                        <Plus className="w-4 h-4" />
                        Add Instruction
                      </Button>
                    )}
                  </CardContent>
                </Card> */}

                {/* Output Format */}
                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg w-full">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileOutput className="w-4 h-4" />
                      Output Format
                    </CardTitle>
                    <CardDescription>
                      Configure the expected output format and structure
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Format Guidelines */}
                    <div className="space-y-2">
                      <Label className="text-sm">Best Practices</Label>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <ul className="space-y-1">
                          {getOutputFormatDetails(formData?.outputFormat)?.guidelines?.map((guideline, index) => (
                            <li key={index} className="text-xs text-gray-600 flex items-start gap-1">
                              <span className="w-1 h-1 bg-gray-400 rounded-full mt-1.5 flex-shrink-0"></span>
                              <span>{guideline}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Editable Output Example */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Output Schema</Label>
                        <div className="flex items-center gap-2 ">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const defaultExample = getOutputFormatDetails(formData.outputFormat).example;
                              setFormData(prev => ({ ...prev, customOutputExample: defaultExample }));
                            }}
                            disabled={!isEditing}
                            className="text-xs h-6 px-2"
                          >
                            Load Default
                          </Button>
                          <span className="text-xs text-gray-500">Editable</span>
                        </div>
                      </div>
                      {/* <Textarea
                        value={formData.customOutputExample || getOutputFormatDetails(formData.outputFormat).example}
                        onChange={(e) => setFormData(prev => ({ ...prev, customOutputExample: e.target.value }))}
                        disabled={false}
                        placeholder={`Enter your custom ${formData.outputFormat} example output...`}
                        rows={8}
                        className="font-mono text-xs  h-[50vh] bg-gray-900 text-gray-500 border-gray-700 placeholder:text-gray-500 resize-y"
                        style={{ minHeight: '500px', maxHeight: '50vh' }}
                      /> */}
                      <CodeEditor
                                value={cleanJSONCodeFn(formData.customOutputExample)}
                                onChange={(e) => setFormData(prev => ({ ...prev, customOutputExample: e}))}
                                // placeholder={
                                //   inputType.value === 'json' ? '{"patient_name": "John Doe", "symptoms": "fever, cough"}' :
                                //     inputType.value === 'csv' ? 'patient_id,name,age,symptoms\n001,John Doe,45,"fever,cough"' :
                                //       'Enter your text input here...'
                                // }
                                language='json'
                                className="min-h-auto h-50vh"
                        />
                      <p className="text-xs text-gray-500">
                        Customize this example to show the exact output format your application expects.
                        This helps other users understand the expected structure and format.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Enhanced Testing Tab with Dynamic Input Types */}
            {/* <TabsContent value="testing" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TestTube className="w-4 h-4" />
                      Test Prompt
                    </CardTitle>
                    <CardDescription>
                      Test with inputs optimized for <Badge variant="secondary" className="mx-1">{formData.outputFormat}</Badge> format using {formData.targetModel}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    // Dynamic Input Type Tabs 
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Input Type</Label>
                        <Badge variant="outline" className="text-xs">
                          {availableInputTypes.length} options for {formData.outputFormat}
                        </Badge>
                      </div>
                      <Tabs value={testInputType} onValueChange={(value) => setTestInputType(value as any)} className="w-full">
                        <TabsList className={`grid w-full ${availableInputTypes.length === 2 ? 'grid-cols-2' : availableInputTypes.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                          {availableInputTypes.map((inputType) => {
                            const IconComponent = inputType.icon;
                            return (
                              <TabsTrigger key={inputType.value} value={inputType.value} className="flex items-center gap-1">
                                <IconComponent className="w-3 h-3" />
                                {inputType.label}
                              </TabsTrigger>
                            );
                          })}
                        </TabsList>

                        {availableInputTypes.map((inputType) => (
                          <TabsContent key={inputType.value} value={inputType.value} className="space-y-2 mt-4">
                            <div className="flex items-center justify-between">
                              <Label htmlFor={`${inputType.value}Input`}>{inputType.label} Input</Label>
                              <div className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                Output: {formData.outputFormat}
                              </div>
                            </div>

                            {inputType.value === 'file' ? (
                              <>
                                <FileUploadInput
                                  onFileSelect={handleFileSelect}
                                  disabled={isNewPrompt && !formData.name}
                                />
                                {testInputs.file && (
                                  <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                    <FileText className="w-4 h-4 text-blue-600" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-blue-900">{testInputs.file.name}</p>
                                      <p className="text-xs text-blue-600">
                                        {(testInputs.file.size / 1024).toFixed(1)} KB • {testInputs.file.type || 'Unknown type'}
                                      </p>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setTestInputs(prev => ({ ...prev, file: null }))}
                                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <CodeEditor
                                value={testInputs[inputType.value as keyof typeof testInputs] as string}
                                onChange={(value) => setTestInputs(prev => ({ ...prev, [inputType.value]: value }))}
                                placeholder={
                                  inputType.value === 'json' ? '{"patient_name": "John Doe", "symptoms": "fever, cough"}' :
                                    inputType.value === 'csv' ? 'patient_id,name,age,symptoms\n001,John Doe,45,"fever,cough"' :
                                      'Enter your text input here...'
                                }
                                language={inputType.value}
                                className="min-h-96"
                              />
                            )}

                            <p className="text-xs text-gray-500 bg-gray-50 p-2 rounded border">
                              <strong>Purpose:</strong> {inputType.description}
                            </p>
                          </TabsContent>
                        ))}
                      </Tabs>
                    </div>

                    // Run Test Button - Moved to bottom
                    <div className="pt-4 border-t">
                      <Button
                        onClick={handleTest}
                        disabled={isTesting || (isNewPrompt && !formData.name)}
                        className="w-full flex items-center gap-2"
                        size="lg"
                      >
                        <Play className="w-4 h-4" />
                        {isTesting ? 'Testing...' : `Run Test (${formData.targetModel})`}
                      </Button>

                      {isNewPrompt && !formData.name && (
                        <p className="text-sm text-gray-500 text-center mt-2">Save the prompt first to enable testing</p>
                      )}

                      <div className="text-xs text-gray-500 text-center mt-2">
                        Input: {testInputType.toUpperCase()} → Output: {formData.outputFormat}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Test Results
                    </CardTitle>
                    <CardDescription>
                      Recent test executions and their results
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-96">
                      {testResults.length === 0 ? (
                        <div className="text-center py-8">
                          <TestTube className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500">No test results yet</p>
                          <p className="text-sm text-gray-400">Run a test to see results here</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {testResults.map((result) => (
                            <div key={result.id} className="border rounded-lg p-4 space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {result.success ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                  ) : (
                                    <AlertTriangle className="w-4 h-4 text-red-600" />
                                  )}
                                  <span className="text-sm font-medium">
                                    {result.success ? 'Success' : 'Failed'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                  <Clock className="w-3 h-3" />
                                  {result.responseTime.toFixed(0)}ms
                                </div>
                              </div>
                              <div className="text-sm">
                                <p className="text-gray-600 mb-2">Output:</p>
                                <div className="bg-gray-50 p-2 rounded text-xs font-mono">
                                  {result.output.substring(0, 200)}
                                  {result.output.length > 200 && '...'}
                                </div>
                              </div>
                              <div className="text-xs text-gray-500">
                                {result.timestamp.toLocaleString()} • {result.model}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>
            </TabsContent> */}

            {/* History Tab - Only for existing prompts */}
            
            {!isNewPrompt && (
                          <TabsContent value="history" className="space-y-6">
                            <div className={`flex ${comparisonMode === 'fullwidth' && showVersionComparison ? 'flex-col' : 'flex-col lg:flex-row'} gap-6`}>
                              {/* Version History */}
                              <div className={`${showVersionComparison && comparisonMode === 'split' ? 'lg:w-1/2' : 'w-full'} transition-all duration-300`}>
                                <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg">
                                  <CardHeader>
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <CardTitle className="flex items-center gap-2">
                                          <Clock className="w-4 h-4" />
                                          Version History
                                        </CardTitle>
                                        <CardDescription>
                                          Track changes and versions of this prompt over time
                                        </CardDescription>
                                      </div>
                                      {versionHistory.length > 1 && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setShowVersionComparison(!showVersionComparison)}
                                          className="flex items-center gap-2"
                                        >
                                          <ArrowLeftRight className="w-4 h-4" />
                                          {showVersionComparison ? 'Hide Comparison' : 'Compare Versions'}
                                        </Button>
                                      )}
                                    </div>
                                  </CardHeader>
                                  <CardContent>
                                    <div className="space-y-4">
                                      {versionHistory.map((version, index) => (
                                        <div key={index} className="flex items-start gap-4 pb-4 border-b last:border-b-0">
                                          <div className="bg-blue-100 rounded-full p-2 flex-shrink-0">
                                            <GitBranch className="w-4 h-4 text-blue-600" />
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-2">
                                              <div className="flex items-center gap-2">
                                                {showVersionComparison && (
                                                  <div className="flex items-center gap-1">
                                                    <input
                                                      type="radio"
                                                      id={`v1-${version?.version}`}
                                                      name="version1"
                                                      value={version?.version}
                                                      checked={selectedVersions.version1 === version?.version}
                                                      onChange={(e) => {setSelectedVersions(prev => ({ ...prev, version1: e.target.value })),fetchPromptContent(version),analyzeJSON('v1', version)}}
                                                      className="w-4 h-4
                                                                appearance-none
                                                                rounded-full
                                                                bg-white
                                                                border border-gray-400
                                                                relative
                                                                checked:border-blue-600
                                                                after:content-['']
                                                                cursor-pointer
                                                                after:w-2 after:h-2
                                                                after:block
                                                                after:bg-blue-600
                                                                after:rounded-full
                                                                after:top-1/2 after:left-1/2
                                                                after:absolute
                                                                after:scale-0
                                                                after:-translate-x-1/2 after:-translate-y-1/2
                                                                checked:after:scale-100"
                                                    />
                                                    <input
                                                      type="radio"
                                                      id={`v2-${version.version}`}
                                                      name="version2"
                                                      value={version?.version}
                                                      checked={selectedVersions.version2 === version?.version}
                                                      onChange={(e) =>{ setSelectedVersions(prev => ({ ...prev, version2: e.target.value })), fetchPromptContent(version),analyzeJSON('v2', version)}}
                                                      className=" w-4 h-4
                                                      rounded-full
                                                      appearance-none
                                                      bg-white
                                                      border border-gray-400
                                                      relative
                                                      checked:border-blue-600
                                                      after:content-['']
                                                      cursor-pointer
                                                      after:w-2 after:h-2
                                                      after:block
                                                      after:bg-blue-600
                                                      after:rounded-full
                                                      after:top-1/2 after:left-1/2
                                                      after:absolute
                                                      after:scale-0
                                                      after:-translate-x-1/2 after:-translate-y-1/2
                                                      checked:after:scale-100"
                                                    />
                                                  </div>
                                                )}
                                                <h4 className="font-medium">Version {version?.version}</h4>
                                                {index === 0 && (
                                                  <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                                                    Current
                                                  </Badge>
                                                )}
                                              </div>
                                              {/* <div className="flex items-center gap-2">
                                                <Button variant="outline" size="sm">
                                                  <Eye className="w-3 h-3 mr-1" />
                                                  View
                                                </Button>
                                                {index > 0 && (
                                                  <Button variant="outline" size="sm">
                                                    <Download className="w-3 h-3 mr-1" />
                                                    Restore
                                                  </Button>
                                                )}
                                              </div> */}
                                            </div>
                                            <p className="text-sm text-gray-600 mb-2">{version.changes}</p>
                                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                              <span>{version?.author}</span>
                                              <span>•</span>
                                              <span>{version.date?.toLocaleDateString()}</span>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    
                                    {showVersionComparison && (
                                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                        <div className="flex items-center gap-2 text-sm text-blue-900">
                                          <Info className="w-4 h-4" />
                                          <span>Select two versions using the radio buttons to compare them side-by-side.</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-blue-700 mt-1">
                                          <div className="flex items-center gap-1">
                                            <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                                            <span>Base version</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                                            <span>Compare version</span>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              </div>
            
                              {/* Enhanced Version Comparison Panel */}
                              {showVersionComparison && (
                                <div className={`${comparisonMode === 'fullwidth' ? 'w-full' : 'lg:w-1/2'} transition-all duration-300`}>
                                  <Card className="bg-white/90 backdrop-blur-sm border-gray-200 shadow-lg h-full">
                                    <CardHeader>
                                      <div className="space-y-4">
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <CardTitle className="flex items-center gap-2">
                                              <Columns2 className="w-4 h-4" />
                                              Version Comparison
                                            </CardTitle>
                                            <CardDescription>
                                              {selectedVersions?.version1 && selectedVersions?.version2
                                                ? `Comparing v${selectedVersions?.version1} vs v${selectedVersions?.version2}`
                                                : 'Select two versions to compare'}
                                            </CardDescription>
                                          </div>
                                          
                                          {selectedVersions.version1 && selectedVersions.version2 && (
                                            <div className="flex items-center gap-2">
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setComparisonMode(comparisonMode === 'split' ? 'fullwidth' : 'split')}
                                                className="flex items-center gap-2"
                                              >
                                                {comparisonMode === 'split' ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
                                                {comparisonMode === 'split' ? 'Expand' : 'Split View'}
                                              </Button>
                                            </div>
                                          )}
                                        </div>
            
                                        {selectedVersions?.version1 && selectedVersions?.version2 && (
                                          <>
                                            {/* Comparison Controls */}
                                            <div className="flex flex-col sm:flex-row gap-4">
                                              <Tabs value={comparisonTab} onValueChange={(value) => setComparisonTab(value as 'prompt' | 'output')} className="flex-1">
                                                <TabsList className="grid w-full grid-cols-2">
                                                  <TabsTrigger value="prompt" className="flex items-center gap-2">
                                                    <MessageSquare className="w-4 h-4" />
                                                    Prompt Content
                                                  </TabsTrigger>
                                                  <TabsTrigger value="output" className="flex items-center gap-2">
                                                    <Code className="w-4 h-4" />
                                                    Output Format
                                                  </TabsTrigger>
                                                </TabsList>
                                              </Tabs>
                                            </div>
            
                                            {/* Comparison Tools */}
                                            <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                              <div className="flex items-center gap-2">
                                                <Search className="w-4 h-4 text-gray-500" />
                                                <Input
                                                  placeholder="Search in comparison..."
                                                  value={searchTerm}
                                                  onChange={(e) => setSearchTerm(e.target.value)}
                                                  className="w-48 h-8"
                                                />
                                              </div>
                                              
                                              <div className="flex items-center gap-2">
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  onClick={() => setShowLineNumbers(!showLineNumbers)}
                                                  className="flex items-center gap-1"
                                                >
                                                  <Hash className="w-3 h-3" />
                                                  {showLineNumbers ? 'Hide' : 'Show'} Lines
                                                </Button>
                                              </div>
            
                                              <div className="flex items-center gap-2">
                                                <Button
                                                  variant={comparisonView === 'side-by-side' ? 'default' : 'outline'}
                                                  size="sm"
                                                  onClick={() => setComparisonView('side-by-side')}
                                                  className="flex items-center gap-1"
                                                >
                                                  <Columns2 className="w-3 h-3" />
                                                  Side-by-Side
                                                </Button>
                                                <Button
                                                  variant={comparisonView === 'unified' ? 'default' : 'outline'}
                                                  size="sm"
                                                  onClick={() => setComparisonView('unified')}
                                                  className="flex items-center gap-1"
                                                >
                                                  <GitCommit className="w-3 h-3" />
                                                  Unified Diff
                                                </Button>
                                              </div>
            
                                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                                <div className="flex items-center gap-1">
                                                  <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
                                                  <span>v{selectedVersions?.version1}</span>
                                                </div>
                                                <span className="mx-2">•</span>
                                                <div className="flex items-center gap-1">
                                                  <div className="w-3 h-3 bg-red-600 rounded-full"></div>
                                                  <span>v{selectedVersions?.version2}</span>
                                                </div>
                                              </div>
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    </CardHeader>
                                    
                                    <CardContent className="p-0">
                                      {selectedVersions?.version1 && selectedVersions?.version2 ? (
                                        <div className="h-[600px] overflow-hidden">
                                          <ScrollArea className="h-full">
                                            <div className="p-6 space-y-6">
                                              {/* Version Information Cards */}
                                              <div className="grid grid-cols-2 gap-4">
                                                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                                  <div className="flex items-center gap-2 mb-3">
                                                    <div className="w-4 h-4 bg-blue-600 rounded-full"></div>
                                                    <h4 className="font-medium text-blue-900">Version {selectedVersions?.version1}</h4>
                                                  </div>
                                                  {(() => {
                                                    const v1 = versionHistory.find(v => v?.version === selectedVersions?.version1);
                                                    return v1 ? (
                                                      <div className="text-sm text-blue-700">
                                                        <p className="font-medium">{v1?.author}</p>
                                                        <p className="text-xs opacity-75">{v1?.date?.toLocaleDateString()}</p>
                                                        <p className="mt-2 text-xs">{v1?.changes}</p>
                                                      </div>
                                                    ) : null;
                                                  })()}
                                                </div>
                                                
                                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                                  <div className="flex items-center gap-2 mb-3">
                                                    <div className="w-4 h-4 bg-red-600 rounded-full"></div>
                                                    <h4 className="font-medium text-red-900">Version {selectedVersions?.version2}</h4>
                                                  </div>
                                                  {(() => {
                                                    const v2 = versionHistory?.find(v => v?.version === selectedVersions?.version2);
                                                    return v2 ? (
                                                      <div className="text-sm text-red-700">
                                                        <p className="font-medium">{v2?.author}</p>
                                                        <p className="text-xs opacity-75">{v2?.date?.toLocaleDateString()}</p>
                                                        <p className="mt-2 text-xs">{v2?.changes}</p>
                                                      </div>
                                                    ) : null;
                                                  })()}
                                                </div>
                                              </div>
            
                                              {/* Enhanced Content Comparison */}
                                              {comparisonTab === 'prompt' && (
                                                <div className="space-y-6">
                                                  <div className="flex items-center justify-between">
                                                    <h5 className="font-medium text-gray-900">Prompt Content Comparison</h5>
                                                    <div className="text-sm text-gray-500">
                                                      {(() => {
                                                        const v1 = versionHistory.find(v => v?.version === selectedVersions?.version1);
                                                        const v2 = versionHistory.find(v => v?.version === selectedVersions?.version2);
                                                        const v1Content = v1?.promptContent || '';
                                                        const v2Content = v2?.promptContent || '';
                                                        const v1Words = v1Content.split(/\s+/)?.length;
                                                        const v2Words = v2Content.split(/\s+/)?.length;
                                                        return `${v1Words} → ${v2Words} words`;
                                                      })()}
                                                    </div>
                                                  </div>
                                                  
                                                  {comparisonView === 'side-by-side' ? (
                                                    /* Side-by-Side Layout */
                                                    <div className="grid grid-cols-2 gap-4 h-[500px]">
                                                    {(() => {
                                                      const v1 = versionHistory.find(v => v?.version === selectedVersions?.version1);
                                                      const v2 = versionHistory.find(v => v?.version === selectedVersions?.version2);
                                                      const v1Content = v1?.promptContent || 'No content available';
                                                      const v2Content = v2?.promptContent || 'No content available';
                                                      
                                                      return (
                                                        <>
                                                          {/* Version 1 Panel */}
                                                          <div className="relative bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-200 overflow-hidden shadow-sm">
                                                            {/* Header */}
                                                            <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
                                                              <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                  <div className="w-3 h-3 bg-white rounded-full"></div>
                                                                  <div>
                                                                    <h6 className="font-semibold">Version {selectedVersions?.version1}</h6>
                                                                    <p className="text-blue-100 text-xs">
                                                                      {v1Content.split(/\s+/).length} words • {v1Content?.split('\n')?.length} lines
                                                                    </p>
                                                                  </div>
                                                                </div>
                                                                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                                                                  Base
                                                                </Badge>
                                                              </div>
                                                            </div>
                                                            
                                                            {/* Content */}
                                                            <ScrollArea className="h-[420px]" viewportRef={compareDiv}
  syncRef={baseDiv}
 totalLines={v1Content?.split("\n").length ?? 0}
  syncTotalLines={v2Content?.split("\n").length ?? 0} showLineNumber={showLineNumbers} ScrollAreaYAndXDirection={true}>
                                                              <div className="p-4 ">
                                                                {showLineNumbers ? (
                                                                  <div className="flex" >
                                                                    <div className="pr-4 text-blue-600 text-sm font-mono select-none border-r border-blue-300 mr-4 bg-blue-100/50 -ml-4 pl-4 -mr-4 pr-8">
                                                                      <div className="sticky top-0 py-2">
                                                                        {v1Content?.split('\n')?.map((_, i) => (
                                                                          <div key={i} className="leading-6 h-6">{i + 1}</div>
                                                                        ))}
                                                                      </div>
                                                                    </div>
                                                                    <div className="flex-1 " >
                                                                      <pre className="text-sm text-gray-800 pre-wrap font-mono leading-6" >
                                                                        {searchTerm ? 
                                                                          v1Content?.split(new RegExp(`(${searchTerm})`, 'gi'))?.map((part, i) =>
                                                                            part.toLowerCase() === searchTerm.toLowerCase() ? 
                                                                              <mark key={i} className="bg-yellow-300 rounded px-1">{part}</mark> : part
                                                                          ) : v1Content
                                                                        }
                                                                      </pre>
                                                                    </div>
                                                                  </div>
                                                                ) : (
                                                                  <pre className="text-sm text-gray-800 pre-wrap font-mono leading-6">
                                                                    {searchTerm ? 
                                                                      v1Content?.split(new RegExp(`(${searchTerm})`, 'gi')).map((part, i) =>
                                                                        part.toLowerCase() === searchTerm?.toLowerCase() ? 
                                                                          <mark key={i} className="bg-yellow-300 rounded px-1">{part}</mark> : part
                                                                      ) : v1Content
                                                                    }
                                                                  </pre>
                                                                )}
                                                              </div>
                                                            </ScrollArea>
                                                          </div>
                                                          
                                                          {/* Version 2 Panel */}
                                                          <div className="relative bg-gradient-to-br from-rose-50 to-rose-100/50 rounded-xl border border-rose-200 overflow-hidden shadow-sm">
                                                            {/* Header */}
                                                            <div className="sticky top-0 z-10 bg-gradient-to-r from-rose-600 to-rose-700 text-white p-4">
                                                              <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                  <div className="w-3 h-3 bg-white rounded-full"></div>
                                                                  <div>
                                                                    <h6 className="font-semibold">Version {selectedVersions?.version2}</h6>
                                                                    <p className="text-rose-100 text-xs">
                                                                      {v2Content?.split(/\s+/)?.length} words • {v2Content?.split('\n')?.length} lines
                                                                    </p>
                                                                  </div>
                                                                </div>
                                                                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                                                                  Compare
                                                                </Badge>
                                                              </div>
                                                            </div>
                                                            
                                                            {/* Content */}
                                                            <ScrollArea className="h-[420px]" viewportRef={baseDiv} syncRef={compareDiv}  totalLines={v2Content?.split("\n").length ?? 0}
  syncTotalLines={v1Content?.split("\n").length ?? 0} showLineNumber={showLineNumbers} ScrollAreaYAndXDirection={true}>
                                                              <div className="p-4">
                                                                {showLineNumbers ? (
                                                                  <div className="flex">
                                                                    <div className="pr-4 text-rose-600 text-sm font-mono select-none border-r border-rose-300 mr-4 bg-rose-100/50 -ml-4 pl-4 -mr-4 pr-8">
                                                                      <div className="sticky top-0 py-2">
                                                                        {v2Content.split('\n').map((_, i) => (
                                                                          <div key={i} className="leading-6 h-6">{i + 1}</div>
                                                                        ))}
                                                                      </div>
                                                                    </div>
                                                                    <div className="flex-1" >
                                                                      <pre className="text-sm text-gray-800 pre-wrap font-mono leading-6">
                                                                        {searchTerm ? 
                                                                          v2Content?.split(new RegExp(`(${searchTerm})`, 'gi'))?.map((part, i) =>
                                                                            part.toLowerCase() === searchTerm.toLowerCase() ? 
                                                                              <mark key={i} className="bg-yellow-300 rounded px-1">{part}</mark> : part
                                                                          ) : v2Content
                                                                        }
                                                                      </pre>
                                                                    </div>
                                                                  </div>
                                                                ) : (
                                                                  <pre className="text-sm text-gray-800 pre-wrap font-mono leading-6">
                                                                    {searchTerm ? 
                                                                      v2Content?.split(new RegExp(`(${searchTerm})`, 'gi')).map((part, i) =>
                                                                        part.toLowerCase() === searchTerm.toLowerCase() ? 
                                                                          <mark key={i} className="bg-yellow-300 rounded px-1">{part}</mark> : part
                                                                      ) : v2Content
                                                                    }
                                                                  </pre>
                                                                )}
                                                              </div>
                                                            </ScrollArea>
                                                          </div>
                                                        </>
                                                      );
                                                    })()}
                                                    </div>
                                                  ) : (
                                                    /* Unified Diff Layout */
                                                    <div className="h-[500px]">
                                                      {(() => {
                                                        const v1 = versionHistory.find(v => v?.version === selectedVersions?.version1);
                                                        const v2 = versionHistory.find(v => v?.version === selectedVersions?.version2);
                                                        const v1Content = v1?.promptContent || 'No content available';
                                                        const v2Content = v2?.promptContent || 'No content available';
                                                        
                                                        const diffHunks = createUnifiedDiff(v1Content, v2Content);
                                                        
                                                        if (diffHunks?.length === 0 || v1Content === v2Content) {
                                                          return (
                                                            <div className="flex items-center justify-center h-full bg-green-50 border border-green-200 rounded-xl">
                                                              <div className="text-center">
                                                                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                                                                <h6 className="font-medium text-green-900 mb-2">No Differences Found</h6>
                                                                <p className="text-sm text-green-700">The prompt content is identical between these versions.</p>
                                                              </div>
                                                            </div>
                                                          );
                                                        }
                                                        
                                                        return (
                                                          <div className="relative bg-gray-50 rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                                            {/* Diff Header */}
                                                            <div className="sticky top-0 z-10 bg-gray-800 text-gray-100 p-4 border-b border-gray-600">
                                                              <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                  <GitCommit className="w-4 h-4" />
                                                                  <div>
                                                                    <h6 className="font-semibold">Unified Diff</h6>
                                                                    <p className="text-gray-300 text-xs">
                                                                      Version {selectedVersions.version1} → Version {selectedVersions?.version2}
                                                                    </p>
                                                                  </div>
                                                                </div>
                                                                <div className="flex items-center gap-4 text-xs">
                                                                  <div className="flex items-center gap-1">
                                                                    <div className="w-3 h-3 bg-red-500 rounded-sm"></div>
                                                                    <span>Removed</span>
                                                                  </div>
                                                                  <div className="flex items-center gap-1">
                                                                    <div className="w-3 h-3 bg-green-500 rounded-sm"></div>
                                                                    <span>Added</span>
                                                                  </div>
                                                                </div>
                                                              </div>
                                                            </div>
                                                            
                                                            {/* Diff Content */}
                                                            <ScrollArea className="h-[420px]">
                                                              <div className="p-0">
                                                                {diffHunks?.map((hunk, hunkIndex) => (
                                                                  <div key={hunkIndex} className="mb-6">
                                                                    {/* Hunk Header */}
                                                                    <div className="bg-blue-100 border-y border-blue-200 px-4 py-2">
                                                                      <span className="font-mono text-sm text-blue-800">
                                                                        @@ -{hunk?.oldStart},{hunk?.oldCount} +{hunk?.newStart},{hunk?.newCount} @@
                                                                      </span>
                                                                    </div>
                                                                    
                                                                    {/* Hunk Lines */}
                                                                    <div className="bg-white">
                                                                      {hunk?.lines?.map((line, lineIndex) => (
                                                                        <div
                                                                          key={lineIndex}
                                                                          className={`flex items-start ${
                                                                            line.type === 'add' 
                                                                              ? 'bg-green-50 border-l-4 border-green-400' 
                                                                              : line.type === 'remove' 
                                                                              ? 'bg-red-50 border-l-4 border-red-400'
                                                                              : 'bg-white border-l-4 border-transparent'
                                                                          }`}
                                                                        >
                                                                          {showLineNumbers && (
                                                                            <div className="flex-shrink-0 bg-gray-100 px-3 py-1 border-r border-gray-200">
                                                                              <div className="grid grid-cols-2 gap-2 text-xs font-mono text-gray-600 min-w-16">
                                                                                <span className="text-right">
                                                                                  {line.oldLineNumber || ''}
                                                                                </span>
                                                                                <span className="text-right">
                                                                                  {line.newLineNumber || ''}
                                                                                </span>
                                                                              </div>
                                                                            </div>
                                                                          )}
                                                                          <div className="flex-1 px-4 py-1">
                                                                            <div className="flex items-start gap-2">
                                                                              <span className={`font-mono text-sm font-bold flex-shrink-0 ${
                                                                                line.type === 'add' 
                                                                                  ? 'text-green-600' 
                                                                                  : line.type === 'remove' 
                                                                                  ? 'text-red-600'
                                                                                  : 'text-gray-400'
                                                                              }`}>
                                                                                {line?.type === 'add' ? '+' : line?.type === 'remove' ? '-' : ' '}
                                                                              </span>
                                                                              <pre className={`font-mono text-sm whitespace-pre-wrap leading-6 ${
                                                                                line?.type === 'add' 
                                                                                  ? 'text-green-800' 
                                                                                  : line.type === 'remove' 
                                                                                  ? 'text-red-800'
                                                                                  : 'text-gray-700'
                                                                              }`}>
                                                                                {searchTerm ? 
                                                                                  line.content?.split(new RegExp(`(${searchTerm})`, 'gi'))?.map((part, i) =>
                                                                                    part.toLowerCase() === searchTerm.toLowerCase() ? 
                                                                                      <mark key={i} className="bg-yellow-300 rounded px-1">{part}</mark> : part
                                                                                  ) : line.content
                                                                                }
                                                                              </pre>
                                                                            </div>
                                                                          </div>
                                                                        </div>
                                                                      ))}
                                                                    </div>
                                                                  </div>
                                                                ))}
                                                              </div>
                                                            </ScrollArea>
                                                          </div>
                                                        );
                                                      })()}
                                                    </div>
                                                  )}
            
                                                  {/* Enhanced Difference Summary */}
                                                  {(() => {
                                                    const v1 = versionHistory?.find(v => v?.version === selectedVersions?.version1);
                                                    const v2 = versionHistory?.find(v => v?.version === selectedVersions?.version2);
                                                    const v1Content = v1?.promptContent || '';
                                                    const v2Content = v2?.promptContent || '';
                                                    
                                                    if (v1Content === v2Content) {
                                                      return (
                                                        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                                                          <div className="flex items-center gap-2 text-green-900">
                                                            <CheckCircle className="w-4 h-4" />
                                                            <span className="font-medium">Identical Content</span>
                                                          </div>
                                                          <p className="text-sm text-green-700 mt-1">
                                                            The prompt content is identical between these versions.
                                                          </p>
                                                        </div>
                                                      );
                                                    }
                                                    
                                                    const wordDiff = v2Content?.split(/\s+/).length - v1Content?.split(/\s+/).length;
                                                    const charDiff = v2Content?.length - v1Content?.length;
                                                    const lineDiff = v2Content?.split('\n').length - v1Content?.split('\n').length;
                                                    
                                                    return (
                                                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                                                        <div className="flex items-center gap-2 text-amber-900 mb-3">
                                                          <AlertTriangle className="w-4 h-4" />
                                                          <span className="font-medium">Content Differences Detected</span>
                                                        </div>
                                                        <div className="grid grid-cols-3 gap-6">
                                                          <div className="text-center">
                                                            <div className={`text-lg font-bold ${wordDiff > 0 ? 'text-green-600' : wordDiff < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                                              {wordDiff > 0 ? '+' : ''}{wordDiff}
                                                            </div>
                                                            <p className="text-xs text-amber-700 font-medium">Words</p>
                                                            <p className="text-xs text-amber-600">
                                                              {v1Content?.split(/\s+/)?.length} → {v2Content?.split(/\s+/)?.length}
                                                            </p>
                                                          </div>
                                                          <div className="text-center">
                                                            <div className={`text-lg font-bold ${charDiff > 0 ? 'text-green-600' : charDiff < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                                              {charDiff > 0 ? '+' : ''}{charDiff}
                                                            </div>
                                                            <p className="text-xs text-amber-700 font-medium">Characters</p>
                                                            <p className="text-xs text-amber-600">
                                                              {v1Content?.length} → {v2Content?.length}
                                                            </p>
                                                          </div>
                                                          <div className="text-center">
                                                            <div className={`text-lg font-bold ${lineDiff > 0 ? 'text-green-600' : lineDiff < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                                                              {lineDiff > 0 ? '+' : ''}{lineDiff}
                                                            </div>
                                                            <p className="text-xs text-amber-700 font-medium">Lines</p>
                                                            <p className="text-xs text-amber-600">
                                                              {v1Content?.split('\n')?.length} → {v2Content?.split('\n')?.length}
                                                            </p>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    );
                                                  })()}
                                                </div>
                                              )}
            
                                              {comparisonTab === 'output' && (
                                                <div className="space-y-6">
                                                  <div className="flex items-center justify-between">
                                                    <h5 className="font-medium text-gray-900">Output Format Comparison</h5>
                                                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                                      JSON Format
                                                    </Badge>
                                                  </div>
                                                  
                                                  {/* Always Side-by-Side JSON Layout */}
                                                  <div className="grid grid-cols-2 gap-4 h-[500px]">
                                                    {(() => {
                                                      const v1 = versionHistory?.find(v => v?.version === selectedVersions.version1);
                                                      const v2 = versionHistory?.find(v => v?.version === selectedVersions.version2);
                                                      console.log(v1, v2, "v1 and v2")
                                                      // For demo purposes, using different output examples to show comparison
                                                      // const v1OutputExample = getOutputFormatDetails('JSON').example;
                                                      const v1OutputExample = v1?.output_template;
                                                      const v2OutputExample = v2?.output_template;
            //                                           const v2OutputExample = `{
            //   "patient_name": "John Doe",
            //   "patient_id": "PT-12345",
            //   "diagnosis": "Type 2 Diabetes Mellitus",
            //   "severity": "moderate",
            //   "medications": [
            //     {
            //       "name": "Metformin",
            //       "dosage": "500mg",
            //       "frequency": "twice daily",
            //       "start_date": "2024-01-15",
            //       "prescriber": "Dr. Smith"
            //     },
            //     {
            //       "name": "Insulin",
            //       "dosage": "20 units",
            //       "frequency": "before meals",
            //       "start_date": "2024-01-20",
            //       "prescriber": "Dr. Smith"
            //     }
            //   ],
            //   "vitals": {
            //     "blood_pressure": "130/85",
            //     "heart_rate": 78,
            //     "temperature": "98.6°F",
            //     "weight": "185 lbs",
            //     "bmi": 26.8
            //   },
            //   "next_appointment": "2024-02-15",
            //   "follow_up_required": true,
            //   "confidence_score": 0.96,
            //   "extraction_timestamp": "2024-01-10T14:30:00Z",
            //   "source_document": "clinical_note_20240110.pdf"
            // }`;
                                                      
                                                      return (
                                                        <>
                                                          {/* Version 1 JSON Panel */}
                                                          <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-xl border border-emerald-200 overflow-hidden shadow-sm">
                                                            {/* Header */}
                                                            <div className="sticky top-0 z-10 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4">
                                                              <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                  <div className="w-3 h-3 bg-white rounded-full"></div>
                                                                  <div>
                                                                    <h6 className="font-semibold">Version {selectedVersions?.version1}</h6>
                                                                    <p className="text-emerald-100 text-xs">
                                                                      {v1OutputExample?.split('\n')?.length} lines • JSON Schema
                                                                    </p>
                                                                  </div>
                                                                </div>
                                                                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                                                                  Base
                                                                </Badge>
                                                              </div>
                                                            </div>
                                                            
                                                            {/* JSON Content */}
                                                            <ScrollArea className="h-[420px]" viewportRef={compareOutput}
  syncRef={baseOutput}
 totalLines={v1OutputExample?.split("\n").length ?? 0}
  syncTotalLines={v2OutputExample?.split("\n").length ?? 0} showLineNumber={showLineNumbers} ScrollAreaYAndXDirection={true}>
                                                              <div className="p-4">
                                                                {showLineNumbers ? (
                                                                  <div className="flex">
                                                                    <div className="pr-4 text-emerald-600 text-sm font-mono select-none border-r border-emerald-300 mr-4 bg-emerald-100/50 -ml-4 pl-4 -mr-4 pr-8">
                                                                      <div className="sticky top-0 py-2">
                                                                        {v1OutputExample?.split('\n')?.map((_, i) => (
                                                                          <div key={i} className="leading-6 h-6">{i + 1}</div>
                                                                        ))}
                                                                      </div>
                                                                    </div>
                                                                    <div className="flex-1">
                                                                      <pre className="text-sm pre-wrap font-mono leading-6">
                                                                        <code className="language-json text-gray-800">
                                                                          {v1OutputExample}
                                                                        </code>
                                                                      </pre>
                                                                    </div>
                                                                  </div>
                                                                ) : (
                                                                  <pre className="text-sm pre-wrap font-mono leading-6">
                                                                    <code className="language-json text-gray-800">
                                                                      {v1OutputExample}
                                                                    </code>
                                                                  </pre>
                                                                )}
                                                              </div>
                                                            </ScrollArea>
                                                          </div>
                                                          
                                                          {/* Version 2 JSON Panel */}
                                                          <div className="relative bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl border border-purple-200 overflow-hidden shadow-sm">
                                                            {/* Header */}
                                                            <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-600 to-purple-700 text-white p-4">
                                                              <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                  <div className="w-3 h-3 bg-white rounded-full"></div>
                                                                  <div>
                                                                    <h6 className="font-semibold">Version {selectedVersions.version2}</h6>
                                                                    <p className="text-purple-100 text-xs">
                                                                      {v2OutputExample?.split('\n')?.length} lines • Enhanced Schema
                                                                    </p>
                                                                  </div>
                                                                </div>
                                                                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                                                                  Enhanced
                                                                </Badge>
                                                              </div>
                                                            </div>
                                                            
                                                            {/* JSON Content */}
                                                            <ScrollArea className="h-[420px]" viewportRef={baseOutput}
  syncRef={compareOutput}
 totalLines={v1OutputExample?.split("\n").length ?? 0}
  syncTotalLines={v2OutputExample?.split("\n").length ?? 0} showLineNumber={showLineNumbers} ScrollAreaYAndXDirection={true}>
                                                              <div className="p-4">
                                                                {showLineNumbers ? (
                                                                  <div className="flex">
                                                                    <div className="pr-4 text-purple-600 text-sm font-mono select-none border-r border-purple-300 mr-4 bg-purple-100/50 -ml-4 pl-4 -mr-4 pr-8">
                                                                      <div className="sticky top-0 py-2">
                                                                        {v2OutputExample?.split('\n')?.map((_, i) => (
                                                                          <div key={i} className="leading-6 h-6">{i + 1}</div>
                                                                        ))}
                                                                      </div>
                                                                    </div>
                                                                    <div className="flex-1">
                                                                      <pre className="text-sm pre-wrap font-mono leading-6">
                                                                        <code className="language-json text-gray-800">
                                                                          {v2OutputExample}
                                                                        </code>
                                                                      </pre>
                                                                    </div>
                                                                  </div>
                                                                ) : (
                                                                  <pre className="text-sm pre-wrap font-mono leading-6">
                                                                    <code className="language-json text-gray-800">
                                                                      {v2OutputExample}
                                                                    </code>
                                                                  </pre>
                                                                )}
                                                              </div>
                                                            </ScrollArea>
                                                          </div>
                                                        </>
                                                      );
                                                    })()}
                                                  </div>
            
                                                  {/* JSON Schema Analysis */}
                                                  <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl">
                                                    <div className="flex items-center gap-2 text-indigo-900 mb-3">
                                                      <Code className="w-4 h-4" />
                                                      <span className="font-medium">JSON Schema Analysis</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-6">
                                                      <div>
                                                        <h6 className="font-medium text-indigo-800 mb-2">Version {selectedVersions?.version1}</h6>
                                                        <div className="space-y-1 text-xs text-indigo-700">
                                                          <div className="flex justify-between">
                                                            <span>Fields:</span>
                                                            <span className="font-mono">{jsonStats?.v1?.numberOfFields}</span>
                                                          </div>
                                                          <div className="flex justify-between">
                                                            <span>Nested Objects:</span>
                                                            <span className="font-mono">{jsonStats?.v1?.nestedObjects}</span>
                                                          </div>
                                                          <div className="flex justify-between">
                                                            <span>Arrays:</span>
                                                            <span className="font-mono">{jsonStats?.v1?.numberOfArray}</span>
                                                          </div>
                                                        </div>
                                                      </div>
                                                      <div>
                                                        <h6 className="font-medium text-purple-800 mb-2">Version {selectedVersions.version2}</h6>
                                                        <div className="space-y-1 text-xs text-purple-700">
                                                          <div className="flex justify-between">
                                                            <span>Fields:</span>
                                                            <span className="font-mono">{jsonStats?.v2?.numberOfFields}</span>
                                                          </div>
                                                          <div className="flex justify-between">
                                                            <span>Nested Objects:</span>
                                                            <span className="font-mono">{jsonStats?.v2?.nestedObjects}</span>
                                                          </div>
                                                          <div className="flex justify-between">
                                                            <span>Arrays:</span>
                                                            <span className="font-mono">{jsonStats?.v2?.numberOfArray}</span>
                                                          </div>
                                                        </div>
                                                      </div>
                                                    </div>
                                                    {/* <div className="mt-3 p-3 bg-white/50 rounded-lg">
                                                      <p className="text-xs text-indigo-800">
                                                        <strong>Key Changes:</strong> Added patient_id, severity, enhanced medication details with prescriber and dates, added comprehensive vitals section, and metadata fields for better traceability.
                                                      </p>
                                                    </div> */}
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </ScrollArea>
                                        </div>
                                      ) : (
                                        <div className="text-center py-16">
                                          <ArrowLeftRight className="w-16 h-16 text-gray-400 mx-auto mb-6" />
                                          <p className="text-gray-500 mb-2">Select Two Versions to Compare</p>
                                          <p className="text-sm text-gray-400 max-w-md mx-auto">
                                            Use the radio buttons next to each version to select two versions for side-by-side comparison.
                                            Perfect for reviewing changes in large prompts and complex JSON output formats.
                                          </p>
                                        </div>
                                      )}
                                    </CardContent>
                                  </Card>
                                </div>
                              )}
                            </div>
                          </TabsContent>
                        )}
          </Tabs>

          {/* Save Confirmation Dialog */}
          <AlertDialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
            <AlertDialogContent className="max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2" >
                  <Save className="w-5 h-5 text-blue-600" />
                  {isNewPrompt ? 'Create Prompt' : 'Save Changes'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isNewPrompt
                    ? 'Create this prompt and make it available for use?'
                    : 'How would you like to save your changes?'
                  }
                </AlertDialogDescription>
              </AlertDialogHeader>

              {!isNewPrompt && (
                <div className="space-y-3 py-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <div className="font-medium text-blue-900 mb-1">Update Current Version</div>
                        <div className="text-sm text-blue-700">Overwrites the existing prompt configuration</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                      <div>
                        <div className="font-medium text-green-900 mb-1">Save as New Version</div>
                        <div className="text-sm text-green-700">Creates a new version while preserving the current one</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <AlertDialogFooter className="flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                {isNewPrompt ? (
                  <Button
                    // onClick={handleCreatePrompt}
                    onClick={createPromtsApi}
                    disabled={isSaving || !formData.name.trim()}
                    className="w-full sm:w-auto"
                  >
                    {isSaving ? 'Creating...' : 'Create Prompt'}
                  </Button>
                ) : (
                  <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                    <Button
                      onClick={handleUpdateCurrent}
                      disabled={isSaving}
                      variant="secondary"
                      className="w-full sm:w-auto"
                    >
                      {isSaving ? 'Updating...' : 'Update Current'}
                    </Button>
                    <Button
                      onClick={handleUpdateNewVersion}
                      disabled={isSaving}
                      className="w-full sm:w-auto"
                    >
                      {isSaving ? 'Creating...' : 'Save as New Version'}
                    </Button>
                  </div>
                )}
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Delete Confirmation Dialog */}
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Prompt</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete "{promptData?.name}"? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={()=>handleDeletePrompt(promptData?.version_id)} className="bg-red-600 hover:bg-red-700">
                  Archive
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </Layout>
  );
}